import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { Card, Modal, Button } from "react-bootstrap";
import ApiService from "../../../../services/ApiService";
import DynamicEventAssistant from "./DynamicEventAssistant";
import DynamicEventPreview from "./DynamicEventPreview";
import DynamicEventPreviewModal from "./DynamicEventPreviewModal";
import DynamicEventDesignPicker from "./DynamicEventDesignPicker";
import DynamicEventTranslations from "./DynamicEventTranslations";
import {
  normalizeDesignFromEvent,
  EVENTS_PAGE_SIZE,
  PUBLIC_EVENTS_HOST,
} from "./dynamicEventDesigns";
import { translatableFingerprint } from "./dynamicEventTranslatable";
import { FileCopyOutlined as FileCopyOutlinedIcon } from "@mui/icons-material";
import "react-datepicker/dist/react-datepicker.css";
import Editor from "react-simple-code-editor";
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-markup";
import "prismjs/themes/prism.css"; //Example style, you can use another
import ErrorBoundary from "../../ErrorBoundary";
import parse from 'html-react-parser';
import imageUploadLoader from "../../../../images/misc/loader-light-blue-bg.gif";
import { generateICSFile, timeNow, copyTextToClipboard } from "../../../utils";
import { DateTime } from "luxon";
import { AddToCalendarButton } from "add-to-calendar-button-react";

// A list of common IANA time zones. You can expand this if needed.
const timeZoneList = Intl.supportedValuesOf("timeZone");

const CUSTOM_QUESTION_TYPES = {
  TEXT: "text",
  RADIO: "radio",
  CHECKBOX: "checkbox",
};

// Normalize carousel logos to { src, scale } objects (tolerating legacy
// entries that were stored as plain image URL strings).
const normalizeCarouselImages = (images) => {
  if (!Array.isArray(images)) {
    return [];
  }
  return images
    .map((image) => {
      if (typeof image === "string") {
        return { src: image, scale: 100 };
      }
      if (image && image.src) {
        const scale = Number(image.scale);
        return {
          src: image.src,
          scale: Number.isFinite(scale) && scale > 0 ? scale : 100,
          trimEmptySpace: !!image.trimEmptySpace,
          originalSrc: image.originalSrc || undefined,
        };
      }
      return null;
    })
    .filter(Boolean);
};

// "Trim Empty Space" (whitespace trimming) is only available for PNG uploads;
// the backend writes a `<name>-trim.png` next to the source.
const isPngLogoSrc = (src = "") => /\.png(?:$|\?)/i.test(`${src}`.trim());

// Header logos: { src, scale, yOffset, darkSrc, darkMode, trimEmptySpace,
// originalSrc, separatorAfter, link }. `darkSrc` is the light-on-dark variant used
// by designs that place logos on a dark background (e.g. "cover"); `darkMode`
// is "auto" (generated server-side), "custom" (uploaded) or "original" (render
// the normal logo untouched). When `trimEmptySpace` is on, `src` is the
// trimmed copy and `originalSrc` keeps the untrimmed upload so it can be
// restored. `separatorAfter` draws a vertical "pipe" divider between this
// logo and the next one on the live page. `link` is an optional URL the logo
// opens in a new tab.
const HEADER_LOGO_DARK_MODES = ["auto", "custom", "original"];
const normalizeHeaderLogoDarkMode = (value) =>
  HEADER_LOGO_DARK_MODES.includes(value) ? value : "auto";
const normalizeHeaderLogoLink = (value) => {
  const link = `${value || ""}`.trim();
  if (!link.length) {
    return undefined;
  }
  return /^(https?:)?\/\//i.test(link) || /^mailto:/i.test(link)
    ? link
    : `https://${link}`;
};
const normalizeHeaderLogo = (hl = {}) => ({
  src: hl.src,
  scale: hl.scale,
  yOffset: hl.yOffset,
  darkSrc: hl.darkSrc || undefined,
  darkMode: normalizeHeaderLogoDarkMode(hl.darkMode),
  trimEmptySpace: !!hl.trimEmptySpace,
  originalSrc: hl.originalSrc || undefined,
  separatorAfter: !!hl.separatorAfter,
  link: normalizeHeaderLogoLink(hl.link),
});
const headerLogoNeedsDarkVariant = (hl) =>
  !!hl?.src && normalizeHeaderLogoDarkMode(hl.darkMode) === "auto" && !hl.darkSrc;

const createEmptySpeakerProfile = () => ({
  heading: "",
  name: "",
  title: "",
  bio: "",
  image: "",
});

const normalizeSpeakerProfiles = (speakers) => {
  if (!Array.isArray(speakers)) {
    return [];
  }
  return speakers
    .filter((speaker) => speaker && typeof speaker === "object")
    .map((speaker) => ({
      heading: speaker.heading || "",
      name: speaker.name || "",
      title: speaker.title || "",
      bio: speaker.bio || "",
      image: speaker.image || "",
    }));
};

const getNormalizedCustomQuestions = (pageContent = {}) => {
  const customQuestionsRaw = Array.isArray(pageContent?.customQuestions)
    ? pageContent.customQuestions
    : [];

  const normalizedCustomQuestions = customQuestionsRaw
    .map((question, index) => {
      if (!question || typeof question !== "object") {
        return null;
      }
      const questionLabel = `${question.questionLabel || question.label || ""}`.trim();
      if (!questionLabel.length) {
        return null;
      }
      const answerTypeRaw = `${question.answerType || ""}`.toLowerCase().trim();
      const answerType = Object.values(CUSTOM_QUESTION_TYPES).includes(answerTypeRaw)
        ? answerTypeRaw
        : CUSTOM_QUESTION_TYPES.TEXT;
      const options = Array.isArray(question.options)
        ? question.options
            .map((option) => `${option || ""}`.trim())
            .filter((option) => option.length)
        : [];
      return {
        questionId: `${question.questionId || question.id || `custom-question-${index + 1}`}`.trim(),
        questionLabel,
        answerType,
        required: !!question.required,
        options:
          answerType === CUSTOM_QUESTION_TYPES.TEXT
            ? []
            : Array.from(new Set(options)),
      };
    })
    .filter(Boolean);

  if (normalizedCustomQuestions.length) {
    return normalizedCustomQuestions;
  }

  const legacyLabel = `${pageContent?.customInputLabel || ""}`.trim();
  if (!pageContent?.showCustomInput || !legacyLabel.length) {
    return [];
  }

  return [
    {
      questionId: "custom-question-1",
      questionLabel: legacyLabel,
      answerType: CUSTOM_QUESTION_TYPES.TEXT,
      required: !!pageContent?.customInputRequired,
      options: [],
    },
  ];
};

function formatTimeZone(tz) {
  const parts = tz.split("/");
  if (parts.length === 1) return parts[0].replaceAll("_", " ");
  const region = parts[0];
  const city = parts.slice(1).join(", ").replaceAll("_", " ");
  return `${city} (${region})`;
}

const EventForm = ({
  selectedEvent,
  selectedTemplateEvent,
  getEvents,
  events,
  employee,
  updateSetShowModal,
  resetModes,
  onSavedStayOpen, // (savedEvent, pendingLang) => void - after a "save & stay" of a new event
  autoGenerateLang = "", // language to generate as soon as this form mounts (see above)
  onAutoGenerateHandled,
}) => {
  // Default event information
  const defaultEvent = {
    pageUrl: "new-event",
    title: "New Event",
    referralUrls: [],
    googlePolicyOptions: true,
    rubrikPolicyOptions: false,
    apolloAutofill: true,
    registrationClosed: false,
    owners:
      !(selectedEvent && selectedEvent.eventId) && employee && employee.email
        ? [
            {
              email: employee.email,
              fullName: employee.fullName,
              assignedTime: timeNow(),
            },
          ]
        : [],
    pageContent: {
      headerLogos: [
        // {
        //   src: "https://betterblocks.landmarkventures.com:7770/uploads/landmark/misc/1724954034-lockup_googlecloud_fullcolor_rgb_2900x512px.png",
        //   scale: 100,
        // },
      ],
      eventImages: ["https://i.ibb.co/92V0V9S/West2.jpg"],
      expectBullets: [
        "6:00 - 7:00 pm: Cocktails & Networking",
        "7:00 - 7:30 pm: Around the Room Introductions",
        "7:30 - 9:00 pm: Dinner & Discussion",
      ],
      expectText: `Upon registering, our team will follow up with a formal confirmation and calendar hold with more details.`,
      contentSections: [],
      heroHeading: "VIP Dinner: GenAI for Marketing",
      heroSubHeading:
        "Join us on September 10th at RPM Events, Chicago's premier private event venue, for an wonderful evening hosted in partnership with Google Cloud and Typeface.",
      heroText:
        "This special VIP Dinner (invitation-only; complimentary) brings together an exclusive group of top 20-25 CMOs and Senior Marketing Leaders for an insightful conversation around the trends, challenges, and opportunities related to harnessing and maximizing Generative AI. Details as follows:",
      eventDate: "Tuesday, September 10th",
      eventTime: "6:00pm - 9:00pm CDT",
      eventLocationFull:
        "RPM Events (Private Room)\n317 N Clark St\nChicago, IL 60654",
      eventLocationShort: "Chicago, IL",
      eventSpeaker: "",
      enableSpeakerProfiles: false,
      speakerProfiles: [],
      faqs: [
        {
          question:
            "I’d like to meet my peers, will contact information be exchanged?",
          answer:
            "Yes, in the spirit of networking, our tradition is to do around-the-room introductions and offer to connect everyone in attendance via email following the event.",
        },
        {
          question: "Is the dinner complimentary?",
          answer: "Yes, all costs are fully covered - we're delighted to host you as one of our guests.",
        },
        {
          question: "Can I bring a plus one?",
          answer:
            "Due to limited capacity, this event is exclusively invitation-only. We are happy to check in on plus ones on an individual basis. If you'd like to inquire about inviting a colleague, please contact jpatterson@landmarkventures.com to check on availability.",
        },
        {
          question: "Is there parking at the venue?",
          answer:
            "As each venue has different parking arrangements, we'll be sure to include all the relevant parking details in the calendar hold and event reminders.",
        },
        {
          question: "Who do I reach out to if I have further questions?",
          answer:
            "Please reach out to jpatterson@landmarkventures.com if you have any questions ahead of the event.",
        },
      ],
      afterSubmittedText: `A member of our team will be in touch shortly confirming your attendance.`,
      footerImages: [],
      enableLogoCarousel: false,
      logoCarouselType: "fullwidth",
      logoCarouselAnimated: false,
      logoCarouselSidebarHeading: "",
      logoCarouselSidebarHeadingUseThemeColor: false,
      logoCarouselImages: [],
      hideLandmarkHeaderLogo: false,
      showSISHeaderLogo: false,
      hideLandmarkFooter: false,
      themePrimaryColor: "#61999d",
      textClass: "",
      headerTextClass: "",
      businessEmailsOnly: false,
      hideDietaryInput: false,
      showCustomInput: false,
      customInputRequired: false,
      customInputLabel: "Referred By",
      customQuestions: [],
      rsvpButtonText: "RSVP",
      registerButtonText: "Register",
      rsvpButtonUrlRedirect: false,
      rsvpButtonUrl: "",
    },
    calendarOptions: {
      title: "Event",
      description: `Description`,
      startDateTime: new Date("2025-09-10T18:00:00-04:00"), // todo: need to handle timezones properly (DST scuffs the -4)
      endDateTime: new Date("2025-09-10T21:00:00-04:00"),
      location: "Prospect",
      timeZone: "America/New_York",
      manuallyEdited: false,
      showAddToCalendarButton: false,
      organizerName: employee.fullName || "Josh Patterson",
      organizerEmail: employee.email || "jpatterson@landmarkventures.com",
    },
    // calendarOptions: {
    //   title: "VIP Dinner: GenAI for Marketing - Chicago, September 10th",
    //   description: `VIP DINNER: GENAI FOR MARKETING\n
    //   <b>Prospect</b>\n
    //   RPM Events (Private Room)\n317 N Clark St\nChicago, IL 60654\n\n
    //   <b>Tuesday, September 10th</b>\n
    //   6:00 - 7:00 pm: Cocktails & Networking\n\n
    //   <b>Questions?</b>\n
    //   <i>Please feel free to reach out to Kayla Schoerie:<a href="mailto:kschoerie@landmarkventures.com">kschoerie@landmarkventures.com</a></i>`,
    //   startDateTime: new Date("2025-09-10T18:00:00-04:00"), // todo: need to handle timezones properly (DST scuffs the -4)
    //   endDateTime: new Date("2025-09-10T21:00:00-04:00"),
    //   location: "Prospect",
    //   timeZone: "America/New_York",
    //   manuallyEdited: false,
    //   showAddToCalendarButton: true,
    // },
  };

  // State initialization
  const [title, setTitle] = useState(
    selectedEvent?.title || selectedTemplateEvent?.title || defaultEvent.title
  );
  const [pageUrl, setPageUrl] = useState(
    selectedEvent?.pageUrl ||
      selectedTemplateEvent?.pageUrl ||
      defaultEvent.pageUrl
  );

  const [referralUrls, setReferralUrls] = useState(
    selectedEvent?.referralUrls ||
      selectedTemplateEvent?.referralUrls ||
      defaultEvent.referralUrls
  );

  const [googlePolicyOptions, setGooglePolicyOptions] = useState(
    selectedEvent
      ? !!selectedEvent.googlePolicyOptions
      : selectedTemplateEvent
      ? !!selectedTemplateEvent.googlePolicyOptions
      : defaultEvent.googlePolicyOptions
  );

  const [rubrikPolicyOptions, setRubrikPolicyOptions] = useState(
    selectedEvent
      ? !!selectedEvent.rubrikPolicyOptions
      : selectedTemplateEvent
      ? !!selectedTemplateEvent.rubrikPolicyOptions
      : defaultEvent.rubrikPolicyOptions
  );

  const [apolloAutofill, setApolloAutofill] = useState(
    selectedEvent
      ? !!selectedEvent.apolloAutofill
      : selectedTemplateEvent
      ? !!selectedTemplateEvent.apolloAutofill
      : defaultEvent.apolloAutofill
  );

  const [registrationClosed, setRegistrationClosed] = useState(
    selectedEvent
      ? !!selectedEvent.registrationClosed
      : selectedTemplateEvent
      ? !!selectedTemplateEvent.registrationClosed
      : defaultEvent.registrationClosed
  );

  const [headerLogos, setHeaderLogos] = useState(
    selectedEvent?.pageContent?.headerLogos ||
      selectedTemplateEvent?.pageContent?.headerLogos ||
      defaultEvent.pageContent.headerLogos
  );
  const [heroHeading, setHeroHeading] = useState(
    selectedEvent?.pageContent?.heroHeading ||
      selectedTemplateEvent?.pageContent?.heroHeading ||
      defaultEvent.pageContent.heroHeading
  );
  const [heroSubHeading, setHeroSubHeading] = useState(
    selectedEvent?.pageContent?.heroSubHeading ||
      selectedTemplateEvent?.pageContent?.heroSubHeading ||
      defaultEvent.pageContent.heroSubHeading
  );
  const [heroText, setHeroText] = useState(
    selectedEvent?.pageContent?.heroText ||
      selectedTemplateEvent?.pageContent?.heroText ||
      defaultEvent.pageContent.heroText
  );
  const [eventImages, setEventImages] = useState(
    selectedEvent?.pageContent?.eventImages ||
      selectedTemplateEvent?.pageContent?.eventImages ||
      defaultEvent.pageContent.eventImages
  );
  const [eventDate, setEventDate] = useState(
    selectedEvent?.pageContent?.eventDate ||
      selectedTemplateEvent?.pageContent?.eventDate ||
      defaultEvent.pageContent.eventDate
  );
  const [eventTime, setEventTime] = useState(
    selectedEvent?.pageContent?.eventTime ||
      selectedTemplateEvent?.pageContent?.eventTime ||
      defaultEvent.pageContent.eventTime
  );
  const [eventLocationFull, setEventLocationFull] = useState(
    selectedEvent?.pageContent?.eventLocationFull ||
      selectedTemplateEvent?.pageContent?.eventLocationFull ||
      defaultEvent.pageContent.eventLocationFull
  );
  const [eventLocationShort, setEventLocationShort] = useState(
    selectedEvent?.pageContent?.eventLocationShort ||
      selectedTemplateEvent?.pageContent?.eventLocationShort ||
      defaultEvent.pageContent.eventLocationShort
  );
  const [eventSpeaker, setEventSpeaker] = useState(
    selectedEvent?.pageContent?.eventSpeaker ||
      selectedTemplateEvent?.pageContent?.eventSpeaker ||
      defaultEvent.pageContent.eventSpeaker
  );
  const [enableSpeakerProfiles, setEnableSpeakerProfiles] = useState(
    selectedEvent?.pageContent?.enableSpeakerProfiles ??
      selectedTemplateEvent?.pageContent?.enableSpeakerProfiles ??
      defaultEvent.pageContent.enableSpeakerProfiles
  );
  const [speakerProfiles, setSpeakerProfiles] = useState(
    normalizeSpeakerProfiles(
      selectedEvent?.pageContent?.speakerProfiles ||
        selectedTemplateEvent?.pageContent?.speakerProfiles ||
        defaultEvent.pageContent.speakerProfiles
    )
  );
  const [expectBullets, setExpectBullets] = useState(
    selectedEvent?.pageContent?.expectBullets ||
      selectedTemplateEvent?.pageContent?.expectBullets ||
      defaultEvent.pageContent.expectBullets
  );
  const [expectText, setExpectText] = useState(
    selectedEvent?.pageContent?.expectText ||
      selectedTemplateEvent?.pageContent?.expectText ||
      defaultEvent.pageContent.expectText
  );
  const [contentSections, setContentSections] = useState(
    selectedEvent?.pageContent?.contentSections ||
      selectedTemplateEvent?.pageContent?.contentSections ||
      defaultEvent.pageContent.contentSections
  );
  const [faqs, setFaqs] = useState(
    selectedEvent?.pageContent?.faqs ||
      selectedTemplateEvent?.pageContent?.faqs ||
      defaultEvent.pageContent.faqs
  );
  const [afterSubmittedText, setAfterSubmittedText] = useState(
    selectedEvent?.pageContent?.afterSubmittedText ||
      selectedTemplateEvent?.pageContent?.afterSubmittedText ||
      defaultEvent.pageContent.afterSubmittedText
  );
  const [footerImages, setFooterImages] = useState(
    selectedEvent?.pageContent?.footerImages ||
      defaultEvent.pageContent.footerImages
  );
  const [logoCarouselImages, setLogoCarouselImages] = useState(
    normalizeCarouselImages(
      selectedEvent?.pageContent?.logoCarouselImages ||
        selectedTemplateEvent?.pageContent?.logoCarouselImages ||
        defaultEvent.pageContent.logoCarouselImages
    )
  );
  const [enableLogoCarousel, setEnableLogoCarousel] = useState(
    selectedEvent?.pageContent?.enableLogoCarousel ??
      selectedTemplateEvent?.pageContent?.enableLogoCarousel ??
      defaultEvent.pageContent.enableLogoCarousel
  );
  const [logoCarouselType, setLogoCarouselType] = useState(
    selectedEvent?.pageContent?.logoCarouselType ||
      selectedTemplateEvent?.pageContent?.logoCarouselType ||
      defaultEvent.pageContent.logoCarouselType
  );
  const [logoCarouselAnimated, setLogoCarouselAnimated] = useState(
    selectedEvent?.pageContent?.logoCarouselAnimated ??
      selectedTemplateEvent?.pageContent?.logoCarouselAnimated ??
      defaultEvent.pageContent.logoCarouselAnimated
  );
  const [logoCarouselSidebarHeading, setLogoCarouselSidebarHeading] = useState(
    selectedEvent?.pageContent?.logoCarouselSidebarHeading ??
      selectedTemplateEvent?.pageContent?.logoCarouselSidebarHeading ??
      defaultEvent.pageContent.logoCarouselSidebarHeading
  );
  const [
    logoCarouselSidebarHeadingUseThemeColor,
    setLogoCarouselSidebarHeadingUseThemeColor,
  ] = useState(
    selectedEvent?.pageContent?.logoCarouselSidebarHeadingUseThemeColor ??
      selectedTemplateEvent?.pageContent
        ?.logoCarouselSidebarHeadingUseThemeColor ??
      defaultEvent.pageContent.logoCarouselSidebarHeadingUseThemeColor
  );
  const [hideLandmarkHeaderLogo, setHideLandmarkHeaderLogo] = useState(
    selectedEvent?.pageContent?.hideLandmarkHeaderLogo ??
      defaultEvent.pageContent.hideLandmarkHeaderLogo
  );
  const [showSISHeaderLogo, setShowSISHeaderLogo] = useState(
    selectedEvent?.pageContent?.showSISHeaderLogo ??
      defaultEvent.pageContent.showSISHeaderLogo
  );
  const [hideLandmarkFooter, setHideLandmarkFooter] = useState(
    selectedEvent?.pageContent?.hideLandmarkFooter ||
      defaultEvent.pageContent.hideLandmarkFooter
  );
  const [themePrimaryColor, setThemePrimaryColor] = useState(
    selectedEvent?.pageContent?.themePrimaryColor ||
      defaultEvent.pageContent.themePrimaryColor
  );
  const [textClass, setTextClass] = useState(
    selectedEvent?.pageContent?.textClass || defaultEvent.pageContent.textClass
  );
  const [headerTextClass, setHeaderTextClass] = useState(
    selectedEvent?.pageContent?.headerTextClass ||
      defaultEvent.pageContent.headerTextClass
  );
  const [businessEmailsOnly, setBusinessEmailsOnly] = useState(
    selectedEvent?.pageContent?.businessEmailsOnly ||
      defaultEvent.pageContent.businessEmailsOnly
  );
  const [hideDietaryInput, setHideDietaryInput] = useState(
    selectedEvent?.pageContent?.hideDietaryInput ||
      defaultEvent.pageContent.hideDietaryInput
  );
  const initialCustomQuestions = getNormalizedCustomQuestions(
    selectedEvent?.pageContent ||
      selectedTemplateEvent?.pageContent ||
      defaultEvent.pageContent
  );
  const [showCustomInput, setShowCustomInput] = useState(
    initialCustomQuestions.length
      ? true
      : selectedEvent?.pageContent?.showCustomInput ||
          selectedTemplateEvent?.pageContent?.showCustomInput ||
          defaultEvent.pageContent.showCustomInput
  );
  const [customQuestions, setCustomQuestions] = useState(initialCustomQuestions);
  const [customInputRequired, setCustomInputRequired] = useState(
    initialCustomQuestions[0]?.required ||
      selectedEvent?.pageContent?.customInputRequired ||
      defaultEvent.pageContent.customInputRequired
  );
  const [customInputLabel, setCustomInputLabel] = useState(
    initialCustomQuestions[0]?.questionLabel ||
      selectedEvent?.pageContent?.customInputLabel ||
      defaultEvent.pageContent.customInputLabel
  );
  const [rsvpButtonText, setRsvpButtonText] = useState(
    selectedEvent?.pageContent?.rsvpButtonText ||
      selectedTemplateEvent?.pageContent?.rsvpButtonText ||
      defaultEvent.pageContent.rsvpButtonText
  );
  const [registerButtonText, setRegisterButtonText] = useState(
    selectedEvent?.pageContent?.registerButtonText ||
      selectedTemplateEvent?.pageContent?.registerButtonText ||
      defaultEvent.pageContent.registerButtonText
  );
  const [rsvpButtonUrlRedirect, setRsvpButtonUrlRedirect] = useState(
    !!selectedEvent?.pageContent?.rsvpButtonUrlRedirect ||
      !!selectedTemplateEvent?.pageContent?.rsvpButtonUrlRedirect ||
      !!defaultEvent.pageContent.rsvpButtonUrlRedirect
  );
  const [rsvpButtonUrl, setRsvpButtonUrl] = useState(
    selectedEvent?.pageContent?.rsvpButtonUrl ||
      selectedTemplateEvent?.pageContent?.rsvpButtonUrl ||
      defaultEvent.pageContent.rsvpButtonUrl
  );
  const [owners, setOwners] = useState(
    selectedEvent?.owners ||
      selectedTemplateEvent?.owners ||
      defaultEvent.owners
  );
  const [calendarOptions, setCalendarOptions] = useState(
    selectedEvent?.calendarOptions || defaultEvent.calendarOptions
  );

  // Page design + A/B testing (design picker)
  const initialDesign = normalizeDesignFromEvent(
    selectedEvent || selectedTemplateEvent
  );
  const [designId, setDesignId] = useState(initialDesign.designId);
  const [abTestEnabled, setAbTestEnabled] = useState(initialDesign.abTestEnabled);
  const [abTestDesignIds, setAbTestDesignIds] = useState(
    initialDesign.abTestDesignIds
  );
  // Settings only the Cover design reads (see "Cover design options" below)
  const [coverOptions, setCoverOptions] = useState(initialDesign.coverOptions);

  // Generated language versions (only exist on saved events; never copied
  // from a template event because the content will differ)
  const [translations, setTranslations] = useState(
    Array.isArray(selectedEvent?.translations) ? selectedEvent.translations : []
  );
  // fingerprint of the translatable content when the form was opened, so we
  // can warn before saving that translations will go stale (set once the
  // form state has been populated, see effect below getEventObj)
  const [loadedTranslatableFingerprint, setLoadedTranslatableFingerprint] =
    useState(null);
  // fingerprint of the whole form as loaded / last saved -> unsaved changes
  const [loadedEventFingerprint, setLoadedEventFingerprint] = useState(null);

  // large preview modal (opened from a design card / language row):
  // null | { designId?, lang? }
  const [previewModal, setPreviewModal] = useState(null);

  // Loading state
  const [loading, setLoading] = useState(false);
  // page preview
  const [showPreview, setShowPreview] = useState(false);
  const [assistantSaveQueued, setAssistantSaveQueued] = useState(false);
  const [isPageUrlManuallyEdited, setIsPageUrlManuallyEdited] = useState(false);
  // const [previewEvent, setPreviewEvent] = useState(null);

  // Toggle handlers
const handleGooglePolicyChange = (checked) => {
  setGooglePolicyOptions(checked);
  if(checked) {
    setRubrikPolicyOptions(!checked);
  }
};

const handleRubrikPolicyChange = (checked) => {
  setRubrikPolicyOptions(checked);
  if(checked) {
    setGooglePolicyOptions(!checked);
  }
};

  useEffect(() => {
    if (selectedEvent) {
      setTitle(selectedEvent.title || defaultEvent.title);
      setPageUrl(selectedEvent.pageUrl || defaultEvent.pageUrl);

      const loadedDesign = normalizeDesignFromEvent(selectedEvent);
      setDesignId(loadedDesign.designId);
      setAbTestEnabled(loadedDesign.abTestEnabled);
      setAbTestDesignIds(loadedDesign.abTestDesignIds);
      setCoverOptions(loadedDesign.coverOptions);
      setTranslations(
        Array.isArray(selectedEvent.translations) ? selectedEvent.translations : []
      );

      setReferralUrls(selectedEvent.referralUrls || defaultEvent.referralUrls);
      setGooglePolicyOptions(!!selectedEvent.googlePolicyOptions);
      setRubrikPolicyOptions(!!selectedEvent.rubrikPolicyOptions);
      setApolloAutofill(!!selectedEvent.apolloAutofill);
      setRegistrationClosed(!!selectedEvent.registrationClosed);

      setHeaderLogos(
        (selectedEvent.pageContent && selectedEvent.pageContent.headerLogos) ||
          defaultEvent.pageContent.headerLogos
      );
      setHeroHeading(
        (selectedEvent.pageContent && selectedEvent.pageContent.heroHeading) ||
          defaultEvent.pageContent.heroHeading
      );
      setHeroSubHeading(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.heroSubHeading) ||
          defaultEvent.pageContent.heroSubHeading
      );
      setHeroText(
        (selectedEvent.pageContent && selectedEvent.pageContent.heroText) ||
          defaultEvent.pageContent.heroText
      );
      setEventImages(
        (selectedEvent.pageContent && selectedEvent.pageContent.eventImages) ||
          defaultEvent.pageContent.eventImages
      );
      setEventDate(
        (selectedEvent.pageContent && selectedEvent.pageContent.eventDate) ||
          defaultEvent.pageContent.eventDate
      );
      setEventTime(
        (selectedEvent.pageContent && selectedEvent.pageContent.eventTime) ||
          defaultEvent.pageContent.eventTime
      );
      setEventLocationFull(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.eventLocationFull) ||
          defaultEvent.pageContent.eventLocationFull
      );
      setEventLocationShort(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.eventLocationShort) ||
          defaultEvent.pageContent.eventLocationShort
      );
      setEventSpeaker(
        (selectedEvent.pageContent && selectedEvent.pageContent.eventSpeaker) ||
          defaultEvent.pageContent.eventSpeaker
      );
      setEnableSpeakerProfiles(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.enableSpeakerProfiles) ??
          defaultEvent.pageContent.enableSpeakerProfiles
      );
      setSpeakerProfiles(
        normalizeSpeakerProfiles(
          (selectedEvent.pageContent &&
            selectedEvent.pageContent.speakerProfiles) ||
            defaultEvent.pageContent.speakerProfiles
        )
      );
      setExpectBullets(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.expectBullets) ||
          defaultEvent.pageContent.expectBullets
      );
      setExpectText(
        (selectedEvent.pageContent && selectedEvent.pageContent.expectText) ||
          defaultEvent.pageContent.expectText
      );
      setContentSections(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.contentSections) ||
          defaultEvent.pageContent.contentSections
      );
      setFaqs(
        (selectedEvent.pageContent && selectedEvent.pageContent.faqs) ||
          defaultEvent.pageContent.faqs
      );
      setAfterSubmittedText(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.afterSubmittedText) ||
          defaultEvent.pageContent.afterSubmittedText
      );
      setFooterImages(
        (selectedEvent.pageContent && selectedEvent.pageContent.footerImages) ||
          defaultEvent.pageContent.footerImages
      );
      setLogoCarouselImages(
        normalizeCarouselImages(
          (selectedEvent.pageContent &&
            selectedEvent.pageContent.logoCarouselImages) ||
            defaultEvent.pageContent.logoCarouselImages
        )
      );
      setEnableLogoCarousel(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.enableLogoCarousel) ??
          defaultEvent.pageContent.enableLogoCarousel
      );
      setLogoCarouselType(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.logoCarouselType) ||
          defaultEvent.pageContent.logoCarouselType
      );
      setLogoCarouselAnimated(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.logoCarouselAnimated) ??
          defaultEvent.pageContent.logoCarouselAnimated
      );
      setLogoCarouselSidebarHeading(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.logoCarouselSidebarHeading) ??
          defaultEvent.pageContent.logoCarouselSidebarHeading
      );
      setLogoCarouselSidebarHeadingUseThemeColor(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.logoCarouselSidebarHeadingUseThemeColor) ??
          defaultEvent.pageContent.logoCarouselSidebarHeadingUseThemeColor
      );
      setHideLandmarkHeaderLogo(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.hideLandmarkHeaderLogo) ??
          defaultEvent.pageContent.hideLandmarkHeaderLogo
      );
      setShowSISHeaderLogo(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.showSISHeaderLogo) ??
          defaultEvent.pageContent.showSISHeaderLogo
      );
      setHideLandmarkFooter(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.hideLandmarkFooter) ||
          defaultEvent.pageContent.hideLandmarkFooter
      );
      setThemePrimaryColor(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.themePrimaryColor) ||
          defaultEvent.pageContent.themePrimaryColor
      );
      setTextClass(
        (selectedEvent.pageContent && selectedEvent.pageContent.textClass) ||
          defaultEvent.pageContent.textClass
      );
      setHeaderTextClass(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.headerTextClass) ||
          defaultEvent.pageContent.headerTextClass
      );
      setBusinessEmailsOnly(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.businessEmailsOnly) ||
          defaultEvent.pageContent.businessEmailsOnly
      );
      setHideDietaryInput(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.hideDietaryInput) ||
          defaultEvent.pageContent.hideDietaryInput
      );
      const selectedEventCustomQuestions = getNormalizedCustomQuestions(
        selectedEvent.pageContent || {}
      );
      setCustomQuestions(selectedEventCustomQuestions);
      setShowCustomInput(
        selectedEventCustomQuestions.length
          ? true
          : (selectedEvent.pageContent &&
              selectedEvent.pageContent.showCustomInput) ||
              defaultEvent.pageContent.showCustomInput
      );
      setCustomInputRequired(
        selectedEventCustomQuestions[0]?.required ||
          (selectedEvent.pageContent &&
            selectedEvent.pageContent.customInputRequired) ||
          defaultEvent.pageContent.customInputRequired
      );
      setCustomInputLabel(
        selectedEventCustomQuestions[0]?.questionLabel ||
          (selectedEvent.pageContent &&
            selectedEvent.pageContent.customInputLabel) ||
          defaultEvent.pageContent.customInputLabel
      );
      setRsvpButtonText(
        (selectedEvent.pageContent && selectedEvent.pageContent.rsvpButtonText) ||
          defaultEvent.pageContent.rsvpButtonText
      );
      setRegisterButtonText(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.registerButtonText) ||
          defaultEvent.pageContent.registerButtonText
      );
      setRsvpButtonUrlRedirect(
        !!(selectedEvent.pageContent &&
          selectedEvent.pageContent.rsvpButtonUrlRedirect)
      );
      setRsvpButtonUrl(
        (selectedEvent.pageContent &&
          selectedEvent.pageContent.rsvpButtonUrl) ||
          defaultEvent.pageContent.rsvpButtonUrl
      );
      setOwners((selectedEvent && selectedEvent.owners) || defaultEvent.owners);
      setCalendarOptions(
        (selectedEvent && selectedEvent.calendarOptions) ||
          defaultEvent.calendarOptions
      );
    }
  }, [selectedEvent]);

  useEffect(() => {
    if (selectedTemplateEvent) {
      setTitle(selectedTemplateEvent.title || defaultEvent.title);
      setPageUrl(selectedTemplateEvent.pageUrl || defaultEvent.pageUrl);

      setReferralUrls(
        selectedTemplateEvent.referralUrls || defaultEvent.referralUrls
      );
      setGooglePolicyOptions(!!selectedTemplateEvent.googlePolicyOptions);
      setRubrikPolicyOptions(!!selectedTemplateEvent.rubrikPolicyOptions);
      setApolloAutofill(!!selectedTemplateEvent.apolloAutofill);
      setRegistrationClosed(false); // always start with reg open

      setHeaderLogos(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.headerLogos) ||
          defaultEvent.pageContent.headerLogos
      );
      setHeroHeading(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.heroHeading) ||
          defaultEvent.pageContent.heroHeading
      );
      setHeroSubHeading(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.heroSubHeading) ||
          defaultEvent.pageContent.heroSubHeading
      );
      setHeroText(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.heroText) ||
          defaultEvent.pageContent.heroText
      );
      setEventImages(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.eventImages) ||
          defaultEvent.pageContent.eventImages
      );
      setEventDate(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.eventDate) ||
          defaultEvent.pageContent.eventDate
      );
      setEventTime(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.eventTime) ||
          defaultEvent.pageContent.eventTime
      );
      setEventLocationFull(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.eventLocationFull) ||
          defaultEvent.pageContent.eventLocationFull
      );
      setEventLocationShort(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.eventLocationShort) ||
          defaultEvent.pageContent.eventLocationShort
      );
      setEventSpeaker(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.eventSpeaker) ||
          defaultEvent.pageContent.eventSpeaker
      );
      setEnableSpeakerProfiles(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.enableSpeakerProfiles) ??
          defaultEvent.pageContent.enableSpeakerProfiles
      );
      setSpeakerProfiles(
        normalizeSpeakerProfiles(
          (selectedTemplateEvent.pageContent &&
            selectedTemplateEvent.pageContent.speakerProfiles) ||
            defaultEvent.pageContent.speakerProfiles
        )
      );
      setExpectBullets(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.expectBullets) ||
          defaultEvent.pageContent.expectBullets
      );
      setExpectText(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.expectText) ||
          defaultEvent.pageContent.expectText
      );
      setContentSections(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.contentSections) ||
          defaultEvent.pageContent.contentSections
      );
      setFaqs(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.faqs) ||
          defaultEvent.pageContent.faqs
      );
      setAfterSubmittedText(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.afterSubmittedText) ||
          defaultEvent.pageContent.afterSubmittedText
      );
      setFooterImages(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.footerImages) ||
          defaultEvent.pageContent.footerImages
      );
      setLogoCarouselImages(
        normalizeCarouselImages(
          (selectedTemplateEvent.pageContent &&
            selectedTemplateEvent.pageContent.logoCarouselImages) ||
            defaultEvent.pageContent.logoCarouselImages
        )
      );
      setEnableLogoCarousel(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.enableLogoCarousel) ??
          defaultEvent.pageContent.enableLogoCarousel
      );
      setLogoCarouselType(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.logoCarouselType) ||
          defaultEvent.pageContent.logoCarouselType
      );
      setLogoCarouselAnimated(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.logoCarouselAnimated) ??
          defaultEvent.pageContent.logoCarouselAnimated
      );
      setLogoCarouselSidebarHeading(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.logoCarouselSidebarHeading) ??
          defaultEvent.pageContent.logoCarouselSidebarHeading
      );
      setLogoCarouselSidebarHeadingUseThemeColor(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent
            .logoCarouselSidebarHeadingUseThemeColor) ??
          defaultEvent.pageContent.logoCarouselSidebarHeadingUseThemeColor
      );
      setHideLandmarkHeaderLogo(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.hideLandmarkHeaderLogo) ??
          defaultEvent.pageContent.hideLandmarkHeaderLogo
      );
      setShowSISHeaderLogo(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.showSISHeaderLogo) ??
          defaultEvent.pageContent.showSISHeaderLogo
      );
      setHideLandmarkFooter(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.hideLandmarkFooter) ||
          defaultEvent.pageContent.hideLandmarkFooter
      );
      setThemePrimaryColor(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.themePrimaryColor) ||
          defaultEvent.pageContent.themePrimaryColor
      );
      setTextClass(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.textClass) ||
          defaultEvent.pageContent.textClass
      );
      setHeaderTextClass(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.headerTextClass) ||
          defaultEvent.pageContent.headerTextClass
      );
      setBusinessEmailsOnly(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.businessEmailsOnly) ||
          defaultEvent.pageContent.businessEmailsOnly
      );
      setHideDietaryInput(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.hideDietaryInput) ||
          defaultEvent.pageContent.hideDietaryInput
      );
      const templateCustomQuestions = getNormalizedCustomQuestions(
        selectedTemplateEvent.pageContent || {}
      );
      setCustomQuestions(templateCustomQuestions);
      setShowCustomInput(
        templateCustomQuestions.length
          ? true
          : (selectedTemplateEvent.pageContent &&
              selectedTemplateEvent.pageContent.showCustomInput) ||
              defaultEvent.pageContent.showCustomInput
      );
      setCustomInputRequired(
        templateCustomQuestions[0]?.required ||
          (selectedTemplateEvent.pageContent &&
            selectedTemplateEvent.pageContent.customInputRequired) ||
          defaultEvent.pageContent.customInputRequired
      );
      setCustomInputLabel(
        templateCustomQuestions[0]?.questionLabel ||
          (selectedTemplateEvent.pageContent &&
            selectedTemplateEvent.pageContent.customInputLabel) ||
          defaultEvent.pageContent.customInputLabel
      );
      setRsvpButtonText(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.rsvpButtonText) ||
          defaultEvent.pageContent.rsvpButtonText
      );
      setRegisterButtonText(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.registerButtonText) ||
          defaultEvent.pageContent.registerButtonText
      );
      setRsvpButtonUrlRedirect(
        !!(selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.rsvpButtonUrlRedirect)
      );
      setRsvpButtonUrl(
        (selectedTemplateEvent.pageContent &&
          selectedTemplateEvent.pageContent.rsvpButtonUrl) ||
          defaultEvent.pageContent.rsvpButtonUrl
      );
      setOwners(
        (selectedTemplateEvent && selectedTemplateEvent.owners) ||
          defaultEvent.owners
      );
      // manually edited flag is set to false always when loading a template
      setCalendarOptions({
        ...(selectedEvent?.calendarOptions || defaultEvent.calendarOptions),
        manuallyEdited: false,
      });
    }
  }, [selectedTemplateEvent]);

  // Helper functions for dynamic arrays
  const addArrayItem = (setter, array, newItem) => {
    setter([...array, newItem]);
  };

  const removeArrayItem = (setter, array, index) => {
    const updatedArray = array.filter((_, i) => i !== index);
    setter(updatedArray);
  };

  // Speaker profiles
  const handleEnableSpeakerProfilesChange = (checked) => {
    setEnableSpeakerProfiles(checked);
    if (checked && !speakerProfiles.length) {
      setSpeakerProfiles([createEmptySpeakerProfile()]);
    }
  };

  const addSpeakerProfile = () => {
    setSpeakerProfiles((previous) => [...previous, createEmptySpeakerProfile()]);
  };

  const removeSpeakerProfile = (index) => {
    setSpeakerProfiles((previous) =>
      previous.filter((_, speakerIndex) => speakerIndex !== index)
    );
  };

  const updateSpeakerProfile = (index, field, value) => {
    setSpeakerProfiles((previous) =>
      previous.map((speaker, speakerIndex) =>
        speakerIndex === index ? { ...speaker, [field]: value } : speaker
      )
    );
  };

  const moveSpeakerProfile = (index, direction) => {
    setSpeakerProfiles((previous) => {
      const target = index + direction;
      if (target < 0 || target >= previous.length) {
        return previous;
      }
      const updated = [...previous];
      [updated[index], updated[target]] = [updated[target], updated[index]];
      return updated;
    });
  };

  const createCustomQuestionId = () =>
    `custom-question-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const createEmptyCustomQuestion = () => ({
    questionId: createCustomQuestionId(),
    questionLabel: "",
    answerType: CUSTOM_QUESTION_TYPES.TEXT,
    required: false,
    options: [],
  });

  const handleShowCustomInputChange = (checked) => {
    setShowCustomInput(checked);
    if (!checked) {
      setCustomQuestions([]);
      return;
    }
    if (!customQuestions.length) {
      setCustomQuestions([createEmptyCustomQuestion()]);
    }
  };

  const addCustomQuestion = () => {
    setCustomQuestions((previousQuestions) => [
      ...previousQuestions,
      createEmptyCustomQuestion(),
    ]);
  };

  const removeCustomQuestion = (index) => {
    setCustomQuestions((previousQuestions) =>
      previousQuestions.filter((_, questionIndex) => questionIndex !== index)
    );
  };

  const updateCustomQuestion = (index, field, value) => {
    setCustomQuestions((previousQuestions) =>
      previousQuestions.map((question, questionIndex) => {
        if (questionIndex !== index) {
          return question;
        }
        const updatedQuestion = { ...question, [field]: value };
        if (field === "answerType") {
          if (value === CUSTOM_QUESTION_TYPES.TEXT) {
            updatedQuestion.options = [];
          } else if (!Array.isArray(updatedQuestion.options) || !updatedQuestion.options.length) {
            updatedQuestion.options = [""];
          }
        }
        return updatedQuestion;
      })
    );
  };

  const addCustomQuestionOption = (questionIndex) => {
    setCustomQuestions((previousQuestions) =>
      previousQuestions.map((question, currentIndex) => {
        if (currentIndex !== questionIndex) {
          return question;
        }
        const options = Array.isArray(question.options) ? [...question.options] : [];
        options.push("");
        return { ...question, options };
      })
    );
  };

  const updateCustomQuestionOptionValue = (questionIndex, optionIndex, value) => {
    setCustomQuestions((previousQuestions) =>
      previousQuestions.map((question, currentIndex) => {
        if (currentIndex !== questionIndex) {
          return question;
        }
        const options = Array.isArray(question.options) ? [...question.options] : [];
        while (options.length <= optionIndex) {
          options.push("");
        }
        options[optionIndex] = value;
        return { ...question, options };
      })
    );
  };

  const removeCustomQuestionOption = (questionIndex, optionIndex) => {
    setCustomQuestions((previousQuestions) =>
      previousQuestions.map((question, currentIndex) => {
        if (currentIndex !== questionIndex) {
          return question;
        }
        const options = Array.isArray(question.options)
          ? question.options.filter((_, currentOptionIndex) => currentOptionIndex !== optionIndex)
          : [];
        return { ...question, options };
      })
    );
  };

  useEffect(() => {
    if (!customQuestions.length) {
      setCustomInputLabel("");
      setCustomInputRequired(false);
      return;
    }
    setCustomInputLabel(customQuestions[0].questionLabel || "");
    setCustomInputRequired(!!customQuestions[0].required);
  }, [customQuestions]);

  // Calculate rows for textareas based on the approximate word count and newlines
  const calculateRows = (text) => {
    const lines = text.split("\n").length;
    const words = text.split(" ").length;
    return Math.max(lines, Math.ceil(words / 5)); // Adjusting for \n and words per row
  };

  // Calculate dynamic height based on rows
  const calculateHeight = (text) => {
    const rows = calculateRows(text);
    return `${(rows * 65) / 4}px`; // 65px for 2 rows
  };

  // Handle image upload
  const handleImageUpload = async (event, setter, options = {}) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true); // Start loading

    const formData = new FormData();
    formData.append("image", file);
    formData.append("options", JSON.stringify(options));

    try {
      const api = new ApiService({ employee });
      const response = await api.post("landmark/upload/image", formData);
      if (response.success && response.imageUrl) {
        if (options.headerLogoMode) {
          // darkSrc is generated by the auto-backfill effect below
          setter((prev) => [
            ...prev,
            { src: response.imageUrl, scale: 100, yOffset: 0, darkMode: "auto" },
          ]);
        } else if (options.headerLogoDarkMode) {
          // custom light-on-dark version for one header logo
          const targetIndex = options.headerLogoIndex;
          setter((prev) =>
            prev.map((l, i) =>
              i === targetIndex
                ? { ...l, darkSrc: response.imageUrl, darkMode: "custom" }
                : l
            )
          );
        } else if (options.logoCarouselMode) {
          setter((prev) => [...prev, { src: response.imageUrl, scale: 100 }]);
        } else if (options.singleImageMode) {
          // setter receives the url directly (e.g. a speaker profile image)
          setter(response.imageUrl);
        } else {
          setter((prev) => [...prev, response.imageUrl]);
        }
        toast.success("Image uploaded successfully");
      } else {
        toast.error("Failed to upload image");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error uploading image");
    } finally {
      setLoading(false); // End loading
    }
  };

  // ---- Dark (light-on-dark) header logo variants -------------------------
  // Ask the backend for the generated variant of one logo.
  const requestDarkVariant = async (src, { force = false } = {}) => {
    const api = new ApiService({ employee });
    const response = await api.post("landmark/upload/logoDarkVariant", {
      imageUrl: src,
      force,
    });
    if (response?.success && response.imageUrl) {
      // bust browser caches when the file was rewritten in place
      return force ? `${response.imageUrl}?v=${Date.now()}` : response.imageUrl;
    }
    throw new Error(response?.error || "Could not generate dark logo");
  };

  // Patch the "loaded" fingerprint so an automatic backfill of a logo that
  // was loaded without a dark variant doesn't count as an unsaved change.
  const patchLoadedFingerprintHeaderLogos = (mapLogo) => {
    setLoadedEventFingerprint((prev) => {
      if (!prev) return prev;
      try {
        const obj = JSON.parse(prev);
        if (Array.isArray(obj?.pageContent?.headerLogos)) {
          obj.pageContent.headerLogos = obj.pageContent.headerLogos.map((l) =>
            normalizeHeaderLogo(mapLogo(l))
          );
        }
        return JSON.stringify(obj);
      } catch (e) {
        return prev;
      }
    });
  };

  // Auto-generate missing variants for logos in "auto" mode (covers initial
  // load, templates, previously-used logos, AI patches and fresh uploads).
  const darkVariantInFlightRef = useRef(new Set());
  const darkVariantFailedRef = useRef(new Set());
  const [darkVariantsPending, setDarkVariantsPending] = useState(0);
  useEffect(() => {
    const missing = headerLogos.filter(
      (l) =>
        headerLogoNeedsDarkVariant(l) &&
        !darkVariantInFlightRef.current.has(l.src) &&
        !darkVariantFailedRef.current.has(l.src)
    );
    if (!missing.length) return;
    const srcs = Array.from(new Set(missing.map((l) => l.src)));
    srcs.forEach((s) => darkVariantInFlightRef.current.add(s));
    setDarkVariantsPending((n) => n + srcs.length);
    (async () => {
      const results = {};
      await Promise.all(
        srcs.map(async (src) => {
          try {
            results[src] = await requestDarkVariant(src);
          } catch (error) {
            console.error("Dark logo variant failed for", src, error);
            darkVariantFailedRef.current.add(src);
          }
        })
      );
      srcs.forEach((s) => darkVariantInFlightRef.current.delete(s));
      setDarkVariantsPending((n) => Math.max(0, n - srcs.length));
      if (!Object.keys(results).length) return;
      // Applied by src, and only where still missing, so a late result is
      // safe even if the form switched to another event meanwhile.
      const apply = (l) =>
        results[l.src] && headerLogoNeedsDarkVariant(l)
          ? { ...l, darkSrc: results[l.src], darkMode: "auto" }
          : l;
      setHeaderLogos((prev) => prev.map(apply));
      patchLoadedFingerprintHeaderLogos(apply);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerLogos]);

  const regenerateDarkVariant = async (index) => {
    const logo = headerLogos[index];
    if (!logo?.src) return;
    setDarkVariantsPending((n) => n + 1);
    try {
      const darkSrc = await requestDarkVariant(logo.src, { force: true });
      darkVariantFailedRef.current.delete(logo.src);
      setHeaderLogos((prev) =>
        prev.map((l, i) => (i === index ? { ...l, darkSrc, darkMode: "auto" } : l))
      );
      toast.success("Dark logo regenerated");
    } catch (error) {
      toast.error(error.message || "Could not regenerate dark logo");
    } finally {
      setDarkVariantsPending((n) => Math.max(0, n - 1));
    }
  };

  const darkLogoUploadIndexRef = useRef(null);
  const handleDarkModeChange = (index, mode) => {
    const logo = headerLogos[index];
    if (!logo) return;
    if (mode === "custom") {
      // keep the current mode until a file is actually chosen
      darkLogoUploadIndexRef.current = index;
      document.getElementById("headerLogoDarkUpload")?.click();
      return;
    }
    const update = { darkMode: mode };
    if (mode === "auto" && logo.darkMode === "custom") {
      update.darkSrc = undefined; // effect regenerates the auto version
      darkVariantFailedRef.current.delete(logo.src);
    }
    setHeaderLogos((prev) =>
      prev.map((l, i) => (i === index ? { ...l, ...update } : l))
    );
  };

  // What the Cover design will actually render for this logo
  const getDarkLogoRender = (logo) => {
    const mode = normalizeHeaderLogoDarkMode(logo.darkMode);
    if (mode === "original") return { src: logo.src, invert: false };
    if (logo.darkSrc) return { src: logo.darkSrc, invert: false };
    return { src: logo.src, invert: true };
  };

  // ---- "Trim Empty Space" (PNG whitespace trimming) ----------------------
  // Same backend tool the event-options editor uses. We swap `src` for the
  // trimmed copy and keep the untrimmed file in `originalSrc`, so the public
  // designs and the dark-variant pipeline keep working off `src` unchanged.
  const [logoTrimLoading, setLogoTrimLoading] = useState({});

  const requestTrimmedPng = async (src) => {
    const api = new ApiService({ employee });
    const response = await api.post("landmark/upload/trimPngWhitespace", {
      imageUrl: src,
    });
    if (response?.success && response.imageUrl) return response.imageUrl;
    throw new Error(response?.error || "No trimmed image URL returned");
  };

  const handleToggleTrimEmptySpace = async (
    setter,
    logos,
    index,
    checked,
    { isHeaderLogo = false, loadingKey = "" } = {}
  ) => {
    const target = logos[index];
    if (!target) return;
    const key = loadingKey || `${isHeaderLogo ? "header" : "carousel"}-${index}`;
    const patchAt = (patch) =>
      setter((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));

    if (!checked) {
      const restore = {
        trimEmptySpace: false,
        src: target.originalSrc || target.src,
        originalSrc: undefined,
      };
      if (isHeaderLogo && normalizeHeaderLogoDarkMode(target.darkMode) === "auto") {
        // effect regenerates the dark version from the restored file
        restore.darkSrc = undefined;
        darkVariantFailedRef.current.delete(restore.src);
      }
      patchAt(restore);
      return;
    }

    const sourceSrc = target.originalSrc || target.src;
    if (!isPngLogoSrc(sourceSrc)) {
      toast.error("Trim Empty Space is only supported for PNG logos");
      return;
    }

    setLogoTrimLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const trimmedSrc = await requestTrimmedPng(sourceSrc);
      const update = {
        trimEmptySpace: true,
        src: trimmedSrc,
        originalSrc: sourceSrc,
      };
      if (isHeaderLogo) {
        const darkMode = normalizeHeaderLogoDarkMode(target.darkMode);
        if (darkMode === "auto") {
          update.darkSrc = undefined; // regenerated from the trimmed file
          darkVariantFailedRef.current.delete(trimmedSrc);
        } else if (darkMode === "custom" && isPngLogoSrc(target.darkSrc)) {
          // best effort: trim the uploaded dark version too
          try {
            update.darkSrc = await requestTrimmedPng(target.darkSrc);
          } catch (error) {
            console.error("Could not trim custom dark logo:", error);
          }
        }
      }
      patchAt(update);
      toast.success("Trimmed logo is ready");
    } catch (error) {
      console.error("Error trimming logo whitespace:", error);
      toast.error("Failed to trim logo whitespace");
    } finally {
      setLogoTrimLoading((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  // Plain render helper (not a component) so it isn't remounted on every render.
  const renderTrimEmptySpaceToggle = ({ id, checked, trimming, onChange }) => (
    <div className="trim-empty-space-container">
      <div className="trim-empty-space-input">
        <label htmlFor={id} className="trim-empty-space-inline">
          <input
            id={id}
            type="checkbox"
            checked={!!checked}
            onChange={(e) => onChange(e.target.checked)}
            disabled={!!trimming}
          />
          <span>Trim Empty Space</span>
        </label>
        {trimming ? (
          <span className="ms-2 d-flex align-items-center">
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
            <span className="ms-2">Trimming...</span>
          </span>
        ) : null}
      </div>
    </div>
  );

  // Page URL sanitization function
  const sanitizePageUrl = (url) => {
    const sanitizedUrl = url
      .replace(/[^a-zA-Z0-9-]/g, "")
      .toLowerCase()
      .trim(); // Remove invalid characters and convert to lowercase
    if (!selectedEvent || !selectedEvent.eventId) {
      if (events && events.length) {
        const isUnique = !events.some(
          (event) => event.pageUrl.trim().toLowerCase() === sanitizedUrl
        );
        if (!isUnique) {
          // toast.error("Page URL must be unique.");
          // return "";
        }
      }
    }
    return sanitizedUrl;
  };

  const handlePageUrlChange = (e) => {
    const newUrl = e.target.value;
    const sanitizedUrl = sanitizePageUrl(newUrl);
    setPageUrl(sanitizedUrl);
    if (!sanitizedUrl.length) {
      setIsPageUrlManuallyEdited(false);
    } else {
      setIsPageUrlManuallyEdited(true); // Mark that the URL has been manually edited
    }
  };

  const handleSetTitle = (newTitle) => {
    setTitle(newTitle);

    // Only update the pageUrl if it hasn't been manually edited && we are not editing an existing event
    if (
      !pageUrl.length ||
      (!isPageUrlManuallyEdited && !(selectedEvent && selectedEvent.eventId))
    ) {
      const updatedPageUrl = sanitizePageUrl(
        newTitle.trim().toLowerCase().replace(/\s+/g, "-")
      );
      setPageUrl(updatedPageUrl);
    }
  };

  // Add or Edit Event Function.
  // options.stayOpen: keep the form open after saving (used by "Save &
  // generate" in the Languages section). Returns true on success.
  async function addOrEditEvent(event, editMode = false, options = {}) {
    const { stayOpen = false, pendingLang = "" } = options;
    try {
      const api = new ApiService({ employee });
      const url = editMode
        ? "landmark/dynamicevents/editEvent"
        : "landmark/dynamicevents/addEvent";

      console.log(`event being saved`, event);
      const eventsResponse = await api.post(url, { event });
      console.log(`addOrEditEvent() - ${url} - response`, eventsResponse);
      const { success, error, newlyStaleTranslations, staleTranslations } =
        eventsResponse || {};
      if (error) {
        toast.error(error);
        return { ok: false };
      } else if (!success) {
        toast.error("An error occurred");
        return { ok: false };
      } else {
        // toast.success(success);
        const newlyStale = Array.isArray(newlyStaleTranslations)
          ? newlyStaleTranslations
          : [];
        const stillStale = Array.isArray(staleTranslations)
          ? staleTranslations
          : [];
        if (newlyStale.length) {
          toast(
            `Content changed — the ${newlyStale
              .map((l) => l.toUpperCase())
              .join(", ")} language version${
              newlyStale.length > 1 ? "s are" : " is"
            } now out of date. Open the event and regenerate.`,
            { icon: "⚠️", duration: 8000 }
          );
        } else if (stillStale.length) {
          toast(
            `Reminder: the ${stillStale
              .map((l) => l.toUpperCase())
              .join(", ")} language version${
              stillStale.length > 1 ? "s are" : " is"
            } still out of date.`,
            { icon: "⚠️", duration: 6000 }
          );
        }
        if (stayOpen) {
          toast.success("Changes saved");
          getEvents(); // refresh events list in the background
          // the form now matches what is saved
          setLoadedEventFingerprint(JSON.stringify(event));
          setLoadedTranslatableFingerprint(
            translatableFingerprint(event.pageContent)
          );
          if (!editMode && onSavedStayOpen) {
            // created a new event: load the saved record so the form switches
            // into edit mode (gives the Languages section an eventId)
            const saved = await api.post(
              "landmark/dynamicevents/getEventByPageUrl",
              { pageUrl: event.pageUrl }
            );
            if (saved && saved.eventId) {
              onSavedStayOpen(saved, pendingLang);
              return { ok: true, switched: true };
            }
          }
          return { ok: true, switched: false };
        }
        // Show the modal when event is saved (with referral + language links)
        updateSetShowModal(true, {
          pageUrl: event.pageUrl,
          referralUrls: event.referralUrls || [],
          translations: (translations || []).map((t) =>
            newlyStale.includes(t.lang) || stillStale.includes(t.lang)
              ? { ...t, stale: true }
              : t
          ),
        });
        getEvents(); // refresh events
        resetModes(); // exit the edit/create form
        return { ok: true, switched: false };
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to save event.");
      return { ok: false };
    }
  }

  let pageUrlValid = pageUrl && pageUrl.length;
  let pageUrlInvalidMsg = "Page URL cannot be empty";
  if (
    pageUrl &&
    events &&
    events.length &&
    events.some(
      (e) => e.pageUrl.trim().toLowerCase() === pageUrl.trim().toLowerCase()
    )
  ) {
    // page url exists (but we may be editing an existing event)
    if (
      selectedEvent &&
      selectedEvent.eventId &&
      pageUrl === selectedEvent.pageUrl
    ) {
      // ok because we are editing an existing event
    } else {
      pageUrlValid = false;
      pageUrlInvalidMsg = "Page URL already exists";
    }
  }
  console.log(`referralUrls`, referralUrls);
  let referralUrlInvalidMsg = "";
  let referralUrlValid = true;

  if (referralUrls && !referralUrls.every((r) => r.name.trim().length)) {
    referralUrlValid = false;
    referralUrlInvalidMsg = "Referral Name cannot be empty";
  }
  if (referralUrls && !referralUrls.every((r) => r.url.trim().length)) {
    referralUrlValid = false;
    referralUrlInvalidMsg = "Referral URL cannot be empty";
  }
  const refUrlDupeCount = {};
  let refUrlDupeExists = false;
  referralUrls.forEach((r) => {
    const refUrl = r.url.trim().toLowerCase();
    if (refUrl.length) {
      if (!refUrlDupeCount[refUrl]) {
        refUrlDupeCount[refUrl] = true;
      } else {
        // already true - dupe exists
        refUrlDupeExists = true;
      }
    }
  });
  // no duplicate referral urls allowed
  if (referralUrls && referralUrls.length && refUrlDupeExists) {
    referralUrlValid = false;
    referralUrlInvalidMsg = "Referral URL already exists";
  }

  console.log(`referralUrlValid`, referralUrlValid);
  console.log(`referralUrlInvalidMsg`, referralUrlInvalidMsg);

  let ownerEmailInvalidMsg = "";
  let ownerEmailValid = true;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (owners && !owners.every((o) => emailRegex.test(o.email))) {
    ownerEmailValid = false;
    ownerEmailInvalidMsg = "Notification email must be a valid email address";
  }
  if (owners && !owners.every((o) => o.email.trim().length)) {
    ownerEmailValid = false;
    ownerEmailInvalidMsg = "Notification email cannot be empty";
  }
  const ownerEmailDupeCount = {};
  let ownerEmailDupeExists = false;
  owners.forEach((o) => {
    const email = o.email.trim().toLowerCase();
    if (email.length) {
      if (!ownerEmailDupeCount[email]) {
        ownerEmailDupeCount[email] = true;
      } else {
        // already true - dupe exists
        ownerEmailDupeExists = true;
      }
    }
  });
  // no duplicate emails allowed
  if (owners && owners.length && ownerEmailDupeExists) {
    ownerEmailValid = false;
    ownerEmailInvalidMsg = "Notification email already exists";
  }

  console.log(`ownerEmailValid`, ownerEmailValid);
  console.log(`ownerEmailInvalidMsg`, ownerEmailInvalidMsg);

  const getEventObj = () => {
    const customQuestionsForSave = showCustomInput
      ? customQuestions
          .map((question, index) => {
            const questionLabel = `${question.questionLabel || ""}`.trim();
            if (!questionLabel.length) {
              return null;
            }
            const answerTypeRaw = `${question.answerType || ""}`
              .toLowerCase()
              .trim();
            const answerType = Object.values(CUSTOM_QUESTION_TYPES).includes(
              answerTypeRaw
            )
              ? answerTypeRaw
              : CUSTOM_QUESTION_TYPES.TEXT;
            const options = Array.isArray(question.options)
              ? question.options
                  .map((option) => `${option || ""}`.trim())
                  .filter((option) => option.length)
              : [];
            return {
              questionId:
                `${question.questionId || `custom-question-${index + 1}`}`.trim(),
              questionLabel,
              answerType,
              required: !!question.required,
              options:
                answerType === CUSTOM_QUESTION_TYPES.TEXT
                  ? []
                  : Array.from(new Set(options)),
            };
          })
          .filter(Boolean)
      : [];
    const firstCustomQuestion = customQuestionsForSave[0] || null;

    const speakerProfilesForSave = speakerProfiles
      .map((speaker) => ({
        heading: `${speaker.heading || ""}`.trim(),
        name: `${speaker.name || ""}`.trim(),
        title: `${speaker.title || ""}`.trim(),
        bio: `${speaker.bio || ""}`.trim(),
        image: `${speaker.image || ""}`.trim(),
      }))
      .filter((s) => s.heading || s.name || s.title || s.bio || s.image);

    const event = {
      title: title.trim(),
      pageUrl: pageUrl.trim(),
      referralUrls: referralUrls.filter(
        (r) => r.url && r.url.trim().length && r.name && r.name.trim().length
      ),
      googlePolicyOptions,
      rubrikPolicyOptions,
      apolloAutofill,
      registrationClosed,
      design: {
        designId,
        abTest: {
          enabled: !!abTestEnabled,
          designIds: abTestEnabled ? abTestDesignIds : [],
        },
        options: {
          cover: {
            showLandmarkHeaderText: coverOptions.showLandmarkHeaderText !== false,
            showLandmarkFooterText: coverOptions.showLandmarkFooterText !== false,
          },
        },
      },
      owners: owners.map((o) => {
        return { email: o.email, fullName: o.fullName, assignedTime: o.assignedTime }; // strips unwanted __id fields etc
      }),
      calendarOptions,

      pageContent: {
        headerLogos: headerLogos.map(normalizeHeaderLogo), // strips unwanted __id fields etc
        heroHeading: heroHeading.trim(),
        heroSubHeading: heroSubHeading.trim(),
        heroText: heroText.trim(),
        eventImages,
        eventDate: eventDate.trim(),
        eventTime: eventTime.trim(),
        eventLocationFull: eventLocationFull.trim(),
        eventLocationShort: eventLocationShort.trim(),
        eventSpeaker: eventSpeaker ? eventSpeaker.trim() : "",
        enableSpeakerProfiles:
          !!enableSpeakerProfiles && speakerProfilesForSave.length > 0,
        speakerProfiles: speakerProfilesForSave,
        expectBullets: expectBullets.map((b) => b.trim()),
        expectText: expectText.trim(),
        contentSections: contentSections
          .map((s) => {
            return {
              heading: (s.heading || "").trim(),
              text: (s.text || "").trim(),
            }; // strips unwanted __id fields etc
          })
          .filter((s) => s.heading.length || s.text.length),
        faqs: faqs.map((f) => {
          return { question: f.question.trim(), answer: f.answer.trim() };
        }),
        afterSubmittedText: afterSubmittedText.trim(),
        footerImages,
        enableLogoCarousel: enableLogoCarousel && logoCarouselImages.length > 0,
        logoCarouselType:
          logoCarouselType === "sidebar" ? "sidebar" : "fullwidth",
        logoCarouselAnimated: !!logoCarouselAnimated,
        logoCarouselSidebarHeading: logoCarouselSidebarHeading
          ? logoCarouselSidebarHeading.trim()
          : "",
        logoCarouselSidebarHeadingUseThemeColor:
          !!logoCarouselSidebarHeadingUseThemeColor,
        logoCarouselImages: logoCarouselImages.map((logo) => ({
          src: logo.src,
          scale: logo.scale,
          trimEmptySpace: !!logo.trimEmptySpace,
          originalSrc: logo.originalSrc || undefined,
        })),
        hideLandmarkHeaderLogo,
        showSISHeaderLogo,
        hideLandmarkFooter,
        themePrimaryColor,
        textClass: textClass.trim(),
        headerTextClass: headerTextClass.trim(),
        businessEmailsOnly,
        hideDietaryInput,
        showCustomInput: customQuestionsForSave.length > 0,
        customInputRequired: firstCustomQuestion
          ? firstCustomQuestion.required
          : !!customInputRequired,
        customInputLabel: firstCustomQuestion
          ? firstCustomQuestion.questionLabel
          : customInputLabel
          ? customInputLabel.trim()
          : "",
        customQuestions: customQuestionsForSave,
        rsvpButtonText: rsvpButtonText ? rsvpButtonText.trim() : "",
        registerButtonText: registerButtonText ? registerButtonText.trim() : "",
        rsvpButtonUrlRedirect: !!rsvpButtonUrlRedirect,
        rsvpButtonUrl: rsvpButtonUrl ? rsvpButtonUrl.trim() : "",
      },
    };
    return event;
  };

  // Snapshot the translatable content once the form state reflects the
  // loaded event (the sync effect above runs on the same tick). Compared via
  // getEventObj() on both sides so trimming/normalisation doesn't trip it.
  const currentEventObj = getEventObj();
  const currentTranslatableFingerprint = translatableFingerprint(
    currentEventObj.pageContent
  );
  const currentEventFingerprint = JSON.stringify(currentEventObj);
  // latest fingerprints, readable from the deferred snapshot below
  const fingerprintRef = useRef({ event: "", translatable: "" });
  fingerprintRef.current = {
    event: currentEventFingerprint,
    translatable: currentTranslatableFingerprint,
  };
  useEffect(() => {
    // Defer one tick so the "sync state from selectedEvent" effect above has
    // re-rendered the form before we snapshot it.
    const handle = setTimeout(() => {
      setLoadedTranslatableFingerprint(fingerprintRef.current.translatable);
      setLoadedEventFingerprint(fingerprintRef.current.event);
    }, 0);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.eventId]);

  // Unsaved changes: anything differs from what was loaded / last saved. A
  // brand-new (never saved) event always counts as unsaved.
  const hasUnsavedChanges =
    !(selectedEvent && selectedEvent.eventId) ||
    (loadedEventFingerprint !== null &&
      currentEventFingerprint !== loadedEventFingerprint);

  // True when translatable copy differs from what was loaded and the event
  // already has generated language versions -> saving will mark them stale.
  const translationsWillGoStale =
    !!selectedEvent &&
    translations.length > 0 &&
    loadedTranslatableFingerprint !== null &&
    currentTranslatableFingerprint !== loadedTranslatableFingerprint;

  const normalizeAssistantCustomQuestions = (questions = []) => {
    if (!Array.isArray(questions)) {
      return [];
    }
    return questions
      .map((question, index) => {
        const questionLabel = `${question?.questionLabel || ""}`.trim();
        if (!questionLabel.length) {
          return null;
        }
        const answerTypeRaw = `${question?.answerType || ""}`
          .toLowerCase()
          .trim();
        const answerType = Object.values(CUSTOM_QUESTION_TYPES).includes(
          answerTypeRaw
        )
          ? answerTypeRaw
          : CUSTOM_QUESTION_TYPES.TEXT;
        const options = Array.isArray(question?.options)
          ? question.options
              .map((option) => `${option || ""}`.trim())
              .filter((option) => option.length)
          : [];
        return {
          questionId:
            `${question?.questionId || `custom-question-${index + 1}`}`.trim(),
          questionLabel,
          answerType,
          required: !!question?.required,
          options:
            answerType === CUSTOM_QUESTION_TYPES.TEXT
              ? []
              : Array.from(new Set(options)),
        };
      })
      .filter(Boolean);
  };

  const applyAssistantPatch = (fieldPatch = {}, options = {}) => {
    if (!fieldPatch || typeof fieldPatch !== "object") {
      return false;
    }
    const { saveAfterApply = false } = options;
    let hasChanges = false;

    if (typeof fieldPatch.title === "string" && fieldPatch.title.trim().length) {
      handleSetTitle(fieldPatch.title.trim());
      hasChanges = true;
    }

    if (typeof fieldPatch.pageUrl === "string" && fieldPatch.pageUrl.trim().length) {
      const sanitizedUrl = sanitizePageUrl(fieldPatch.pageUrl);
      if (sanitizedUrl.length) {
        setPageUrl(sanitizedUrl);
        setIsPageUrlManuallyEdited(true);
        hasChanges = true;
      }
    }

    if (Array.isArray(fieldPatch.owners)) {
      const ownersNew = fieldPatch.owners
        .map((owner) => {
          if (!owner || typeof owner !== "object") {
            return null;
          }
          const email = `${owner.email || ""}`.trim().toLowerCase();
          if (!email.length) {
            return null;
          }
          return {
            email,
            fullName: `${owner.fullName || ""}`.trim(),
            assignedTime:
              typeof owner.assignedTime === "number"
                ? owner.assignedTime
                : timeNow(),
          };
        })
        .filter(Boolean);
      setOwners(ownersNew);
      hasChanges = true;
    }

    if (Array.isArray(fieldPatch.referralUrls)) {
      const referralUrlsNew = fieldPatch.referralUrls
        .map((ref) => {
          if (!ref || typeof ref !== "object") {
            return null;
          }
          const url = `${ref.url || ""}`.trim();
          const name = `${ref.name || ""}`.trim();
          if (!url.length || !name.length) {
            return null;
          }
          return { url, name };
        })
        .filter(Boolean);
      setReferralUrls(referralUrlsNew);
      hasChanges = true;
    }

    if (typeof fieldPatch.googlePolicyOptions === "boolean") {
      setGooglePolicyOptions(fieldPatch.googlePolicyOptions);
      hasChanges = true;
    }
    if (typeof fieldPatch.rubrikPolicyOptions === "boolean") {
      setRubrikPolicyOptions(fieldPatch.rubrikPolicyOptions);
      hasChanges = true;
    }
    if (typeof fieldPatch.apolloAutofill === "boolean") {
      setApolloAutofill(fieldPatch.apolloAutofill);
      hasChanges = true;
    }
    if (typeof fieldPatch.registrationClosed === "boolean") {
      setRegistrationClosed(fieldPatch.registrationClosed);
      hasChanges = true;
    }

    const pageContentPatch =
      fieldPatch.pageContent && typeof fieldPatch.pageContent === "object"
        ? fieldPatch.pageContent
        : null;
    if (pageContentPatch) {
      if (Array.isArray(pageContentPatch.headerLogos)) {
        const headerLogosNew = pageContentPatch.headerLogos
          .map((logo) => {
            if (!logo || typeof logo !== "object") {
              return null;
            }
            const src = `${logo.src || ""}`.trim();
            if (!src.length) {
              return null;
            }
            return {
              src,
              scale: Number.isFinite(Number(logo.scale))
                ? Number(logo.scale)
                : 100,
              yOffset: Number.isFinite(Number(logo.yOffset))
                ? Number(logo.yOffset)
                : 0,
              darkSrc: logo.darkSrc || undefined,
              darkMode: normalizeHeaderLogoDarkMode(logo.darkMode),
              trimEmptySpace: !!logo.trimEmptySpace,
              originalSrc: logo.originalSrc || undefined,
              separatorAfter: !!logo.separatorAfter,
              link: normalizeHeaderLogoLink(logo.link),
            };
          })
          .filter(Boolean);
        setHeaderLogos(headerLogosNew);
        hasChanges = true;
      }

      if (
        typeof pageContentPatch.heroHeading === "string" &&
        pageContentPatch.heroHeading.trim().length
      ) {
        setHeroHeading(pageContentPatch.heroHeading.trim());
        hasChanges = true;
      }
      if (
        typeof pageContentPatch.heroSubHeading === "string" &&
        pageContentPatch.heroSubHeading.trim().length
      ) {
        setHeroSubHeading(pageContentPatch.heroSubHeading.trim());
        hasChanges = true;
      }
      if (
        typeof pageContentPatch.heroText === "string" &&
        pageContentPatch.heroText.trim().length
      ) {
        setHeroText(pageContentPatch.heroText.trim());
        hasChanges = true;
      }
      if (Array.isArray(pageContentPatch.eventImages)) {
        setEventImages(
          pageContentPatch.eventImages
            .map((image) => `${image || ""}`.trim())
            .filter((image) => image.length)
        );
        hasChanges = true;
      }
      if (
        typeof pageContentPatch.eventDate === "string" &&
        pageContentPatch.eventDate.trim().length
      ) {
        setEventDate(pageContentPatch.eventDate.trim());
        hasChanges = true;
      }
      if (
        typeof pageContentPatch.eventTime === "string" &&
        pageContentPatch.eventTime.trim().length
      ) {
        setEventTime(pageContentPatch.eventTime.trim());
        hasChanges = true;
      }
      if (
        typeof pageContentPatch.eventLocationFull === "string" &&
        pageContentPatch.eventLocationFull.trim().length
      ) {
        setEventLocationFull(pageContentPatch.eventLocationFull.trim());
        hasChanges = true;
      }
      if (
        typeof pageContentPatch.eventLocationShort === "string" &&
        pageContentPatch.eventLocationShort.trim().length
      ) {
        setEventLocationShort(pageContentPatch.eventLocationShort.trim());
        hasChanges = true;
      }
      if (typeof pageContentPatch.eventSpeaker === "string") {
        setEventSpeaker(pageContentPatch.eventSpeaker.trim());
        hasChanges = true;
      }
      if (Array.isArray(pageContentPatch.expectBullets)) {
        setExpectBullets(
          pageContentPatch.expectBullets
            .map((bullet) => `${bullet || ""}`.trim())
            .filter((bullet) => bullet.length)
        );
        hasChanges = true;
      }
      if (typeof pageContentPatch.expectText === "string") {
        setExpectText(pageContentPatch.expectText.trim());
        hasChanges = true;
      }
      if (Array.isArray(pageContentPatch.contentSections)) {
        const contentSectionsNew = pageContentPatch.contentSections
          .map((section) => {
            if (!section || typeof section !== "object") {
              return null;
            }
            const heading = `${section.heading || ""}`.trim();
            const text = `${section.text || ""}`.trim();
            if (!heading.length && !text.length) {
              return null;
            }
            return { heading, text };
          })
          .filter(Boolean);
        setContentSections(contentSectionsNew);
        hasChanges = true;
      }
      if (Array.isArray(pageContentPatch.faqs)) {
        const faqsNew = pageContentPatch.faqs
          .map((faq) => {
            if (!faq || typeof faq !== "object") {
              return null;
            }
            const question = `${faq.question || ""}`.trim();
            const answer = `${faq.answer || ""}`.trim();
            if (!question.length || !answer.length) {
              return null;
            }
            return { question, answer };
          })
          .filter(Boolean);
        setFaqs(faqsNew);
        hasChanges = true;
      }
      if (typeof pageContentPatch.afterSubmittedText === "string") {
        setAfterSubmittedText(pageContentPatch.afterSubmittedText.trim());
        hasChanges = true;
      }
      if (Array.isArray(pageContentPatch.footerImages)) {
        setFooterImages(
          pageContentPatch.footerImages
            .map((image) => `${image || ""}`.trim())
            .filter((image) => image.length)
        );
        hasChanges = true;
      }
      if (typeof pageContentPatch.hideLandmarkHeaderLogo === "boolean") {
        setHideLandmarkHeaderLogo(pageContentPatch.hideLandmarkHeaderLogo);
        hasChanges = true;
      }
      if (typeof pageContentPatch.showSISHeaderLogo === "boolean") {
        setShowSISHeaderLogo(pageContentPatch.showSISHeaderLogo);
        hasChanges = true;
      }
      if (typeof pageContentPatch.hideLandmarkFooter === "boolean") {
        setHideLandmarkFooter(pageContentPatch.hideLandmarkFooter);
        hasChanges = true;
      }
      if (
        typeof pageContentPatch.themePrimaryColor === "string" &&
        pageContentPatch.themePrimaryColor.trim().length
      ) {
        setThemePrimaryColor(pageContentPatch.themePrimaryColor.trim());
        hasChanges = true;
      }
      if (typeof pageContentPatch.textClass === "string") {
        setTextClass(pageContentPatch.textClass.trim());
        hasChanges = true;
      }
      if (typeof pageContentPatch.headerTextClass === "string") {
        setHeaderTextClass(pageContentPatch.headerTextClass.trim());
        hasChanges = true;
      }
      if (typeof pageContentPatch.businessEmailsOnly === "boolean") {
        setBusinessEmailsOnly(pageContentPatch.businessEmailsOnly);
        hasChanges = true;
      }
      if (typeof pageContentPatch.hideDietaryInput === "boolean") {
        setHideDietaryInput(pageContentPatch.hideDietaryInput);
        hasChanges = true;
      }
      if (typeof pageContentPatch.showCustomInput === "boolean") {
        setShowCustomInput(pageContentPatch.showCustomInput);
        if (!pageContentPatch.showCustomInput) {
          setCustomQuestions([]);
        }
        hasChanges = true;
      }
      if (typeof pageContentPatch.customInputRequired === "boolean") {
        setCustomInputRequired(pageContentPatch.customInputRequired);
        hasChanges = true;
      }
      if (typeof pageContentPatch.customInputLabel === "string") {
        setCustomInputLabel(pageContentPatch.customInputLabel.trim());
        hasChanges = true;
      }
      if (Array.isArray(pageContentPatch.customQuestions)) {
        const normalizedQuestions = normalizeAssistantCustomQuestions(
          pageContentPatch.customQuestions
        );
        setCustomQuestions(normalizedQuestions);
        setShowCustomInput(normalizedQuestions.length > 0);
        setCustomInputLabel(normalizedQuestions[0]?.questionLabel || "");
        setCustomInputRequired(!!normalizedQuestions[0]?.required);
        hasChanges = true;
      }
      if (typeof pageContentPatch.rsvpButtonText === "string") {
        setRsvpButtonText(pageContentPatch.rsvpButtonText.trim());
        hasChanges = true;
      }
      if (typeof pageContentPatch.registerButtonText === "string") {
        setRegisterButtonText(pageContentPatch.registerButtonText.trim());
        hasChanges = true;
      }
      if (typeof pageContentPatch.rsvpButtonUrlRedirect === "boolean") {
        setRsvpButtonUrlRedirect(pageContentPatch.rsvpButtonUrlRedirect);
        hasChanges = true;
      }
      if (typeof pageContentPatch.rsvpButtonUrl === "string") {
        setRsvpButtonUrl(pageContentPatch.rsvpButtonUrl.trim());
        hasChanges = true;
      }
    }

    const calendarPatch =
      fieldPatch.calendarOptions && typeof fieldPatch.calendarOptions === "object"
        ? fieldPatch.calendarOptions
        : null;
    if (calendarPatch) {
      const calendarUpdates = {};
      if (
        typeof calendarPatch.title === "string" &&
        calendarPatch.title.trim().length
      ) {
        calendarUpdates.title = calendarPatch.title.trim();
      }
      if (typeof calendarPatch.description === "string") {
        calendarUpdates.description = calendarPatch.description.trim();
      }
      if (typeof calendarPatch.startDateTime === "string") {
        const parsedStartDate = new Date(calendarPatch.startDateTime);
        if (!Number.isNaN(parsedStartDate.getTime())) {
          calendarUpdates.startDateTime = parsedStartDate;
        }
      }
      if (typeof calendarPatch.endDateTime === "string") {
        const parsedEndDate = new Date(calendarPatch.endDateTime);
        if (!Number.isNaN(parsedEndDate.getTime())) {
          calendarUpdates.endDateTime = parsedEndDate;
        }
      }
      if (
        typeof calendarPatch.location === "string" &&
        calendarPatch.location.trim().length
      ) {
        calendarUpdates.location = calendarPatch.location.trim();
      }
      if (
        typeof calendarPatch.timeZone === "string" &&
        calendarPatch.timeZone.trim().length
      ) {
        calendarUpdates.timeZone = calendarPatch.timeZone.trim();
      }
      if (typeof calendarPatch.manuallyEdited === "boolean") {
        calendarUpdates.manuallyEdited = calendarPatch.manuallyEdited;
      }
      if (typeof calendarPatch.showAddToCalendarButton === "boolean") {
        calendarUpdates.showAddToCalendarButton =
          calendarPatch.showAddToCalendarButton;
      }
      if (
        typeof calendarPatch.organizerName === "string" &&
        calendarPatch.organizerName.trim().length
      ) {
        calendarUpdates.organizerName = calendarPatch.organizerName.trim();
      }
      if (
        typeof calendarPatch.organizerEmail === "string" &&
        calendarPatch.organizerEmail.trim().length
      ) {
        calendarUpdates.organizerEmail = calendarPatch.organizerEmail.trim();
      }
      if (Object.keys(calendarUpdates).length) {
        setCalendarOptions((prev) => {
          return { ...prev, ...calendarUpdates };
        });
        hasChanges = true;
      }
    }

    if (!hasChanges) {
      toast.error("Assistant response did not include fields to apply.");
      return false;
    }

    if (saveAfterApply) {
      setAssistantSaveQueued(true);
    }
    return true;
  };

  // function generateCalendarDescriptionHtml(event) {
  //   const heading = event.pageContent.heroHeading || "";
  //   const locationName =
  //     event.pageContent.heroSubHeading
  //       ?.split(" at ")[1]
  //       ?.split(",")[0]
  //       ?.trim() || "";
  //   const fullLocation = event.pageContent.eventLocationFull || "";
  //   const date = event.pageContent.eventDate || "";
  //   const agendaLine = event.pageContent.expectBullets?.[0] || "";

  //   return (
  //     `${heading}<br>` +
  //     `<b>${locationName}</b><br>` +
  //     `${fullLocation.replace(/\n/g, "<br>")}<br><br>` +
  //     `<b>${date}</b><br>` +
  //     `${agendaLine}<br><br>` +
  //     `<b>Questions?</b><br>` +
  //     `<i>Please feel free to reach out to ${employee.fullName}: ` +
  //     `<a href="mailto:${employee.email}">${employee.email}/a></i>`
  //   );
  // }

  // calendar generation useEffect
  useEffect(() => {
    if (!calendarOptions?.manuallyEdited) {
      buildAndSetCalendarOptions();
    }
  }, [
    heroHeading,
    eventLocationFull,
    eventLocationShort,
    eventDate,
    eventTime,
    expectBullets,
    calendarOptions?.manuallyEdited,
    employee,
  ]);

  // this function should be called whenever one of the fields it uses changes - but only when calendarOptions.manuallyEdited is false
  function buildAndSetCalendarOptions() {
    const title = `${heroHeading || "Event"} - ${
      eventLocationShort.split(",")[0] || ""
    }, ${
      eventDate.replace(
        /^(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday),\s*/i,
        ""
      ) || ""
    }`.trim();

    const locationName = "Prospect";

    const fullLocation = (eventLocationFull || "").replace(/\n/g, "<br>\n");

    const agendaLines = (expectBullets || []).join("<br>\n");

    const description =
      `<b>${heroHeading || "Event"}</b><br><br>\n\n` +
      `<b>${locationName}</b><br>\n` +
      `${fullLocation}<br><br>\n\n` +
      `<b>${eventDate}</b><br>\n` +
      `${agendaLines}<br><br>\n\n` +
      `<b>Questions?</b><br>\n` +
      `<i>Please feel free to reach out to ${employee.fullName}: <a href="mailto:${employee.email}">${employee.email}</a></i>`;

    const zone = calendarOptions?.timeZone || "America/New_York"; // EST with DST awareness

    let startDateTime, endDateTime;

    try {
      // --- Parse eventDate and eventTime ---
      // Convert "Tuesday, September 10th" → "September 10" (2025 assumed)
      const cleanedDate = eventDate
        ?.replace(/^\w+,\s*/, "") // remove weekday
        .replace(/(\d+)(st|nd|rd|th)/, "$1"); // remove ordinal suffix

      const currentYear = new Date().getFullYear();

      // Extract start/end times (e.g. "6:00pm - 9:00pm EST")

      // Remove any trailing time zone label (e.g., "EST", "CDT", "ET")
      const [rawStartTime, rawEndTime] = (eventTime || "")
        .replace(/\b[A-Z]{2,4}\b/g, "") // remove any standalone all-uppercase tz labels
        .split("-")
        .map((t) => t.trim());

      startDateTime = DateTime.fromFormat(
        `${currentYear} ${cleanedDate} ${rawStartTime}`,
        "yyyy LLLL d h:mma",
        { zone }
      );
      endDateTime = DateTime.fromFormat(
        `${currentYear} ${cleanedDate} ${rawEndTime}`,
        "yyyy LLLL d h:mma",
        { zone }
      );
    } catch (error) {
      console.log(
        `buildAndSetCalendarOptions() - error parsing event date/time`,
        error
      );
      startDateTime = new Date("2025-09-10T18:00:00-04:00");
      endDateTime = new Date("2025-09-10T21:00:00-04:00");
    }

    const calendarOptionsNew = {
      title,
      description,
      startDateTime,
      endDateTime,
      location: locationName,
      timeZone: zone,
      manuallyEdited: calendarOptions.manuallyEdited,
      showAddToCalendarButton: calendarOptions.showAddToCalendarButton,
      organizerName: calendarOptions.organizerName,
      organizerEmail: calendarOptions.organizerEmail,
    };

    setCalendarOptions(calendarOptionsNew);
    console.log(
      `buildAndSetCalendarOptions() - generating and setting calendarOptions`,
      calendarOptionsNew
    );
  }

  const toggleShowPreview = () => {
    const newShowPreview = !showPreview;
    setShowPreview(newShowPreview);
  };

  const handleSaveEvent = async (options = {}) => {
    const { stayOpen = false, pendingLang = "" } = options;
    const event = getEventObj();

    // hide preview (unless we're saving in place)
    if (!stayOpen) {
      setShowPreview(false);
    }

    if (!pageUrlValid) {
      toast.error("Please enter a valid Page URL");
      return { ok: false };
    }
    if (!referralUrlValid || !ownerEmailValid) {
      toast.error("Please fix the highlighted fields before saving");
      return { ok: false };
    }

    if (selectedEvent && selectedEvent.eventId) {
      event.eventId = selectedEvent.eventId;
      return addOrEditEvent(event, true, { stayOpen, pendingLang }); // edit event
    }
    return addOrEditEvent(event, false, { stayOpen, pendingLang }); // add event
  };

  useEffect(() => {
    if (!assistantSaveQueued) {
      return;
    }
    setAssistantSaveQueued(false);
    setTimeout(() => {
      handleSaveEvent();
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantSaveQueued]);

  let allExistingHeaderLogos = [];
  if (events) {
    const allHeaderLogos = {};
    const otherEvents = selectedEvent
      ? events.filter((e) => e.eventId !== selectedEvent.eventId)
      : events;
    otherEvents.forEach((event) => {
      event.pageContent.headerLogos.forEach((hl) => {
        // prefer an entry that already carries a dark variant
        if (allHeaderLogos[hl.src]?.darkSrc && !hl.darkSrc) {
          return;
        }
        allHeaderLogos[hl.src] = normalizeHeaderLogo(hl);
      });
    });
    allExistingHeaderLogos = Object.values(allHeaderLogos).filter(
      (hl) => !headerLogos.some((chl) => hl.src === chl.src)
    ); // filter to exclude already added logos
  }

  const scaleHeaderLogoMaxWidth = (scale) => {
    const maxWidth = 156; //.ed-wrapper .header-bar .header-container .header-logos .header-logo
    if (scale === 100) {
      return maxWidth;
    } else {
      // Calculate the width based on the scale and round to the nearest pixel
      return Math.round((scale / 100) * maxWidth);
    }
  };

  // Render form inputs based on new schema
  return (
    <>
      <div className="event-form">
        <div className="row">
          {/* Title input */}
          <div className="form-group mb-3 col-md-6">
            <label className="form-label">
              Title <i>(Internal reference only)</i>
            </label>
            <input
              type="text"
              value={title}
              className="form-control"
              onChange={(e) => handleSetTitle(e.target.value)}
            />
          </div>

          {/* Page URL input with prefix */}
          <div className="form-group mb-3 col-md-12">
            <label className="form-label">Page URL</label>
            <div className="input-group">
              <span className="input-group-text">
                https://events.landmarkventures.com/
              </span>
              <input
                type="text"
                value={pageUrl}
                className={`form-control page-url ${
                  pageUrlValid ? "" : "invalid"
                }`}
                onChange={handlePageUrlChange}
              />
            </div>
            {!pageUrlValid ? (
              <div className="page-url-invalid-msg">{pageUrlInvalidMsg}</div>
            ) : null}
          </div>

          {/* Page design picker + A/B testing */}
          <div className="form-group mb-3 col-md-12">
            <DynamicEventDesignPicker
              designId={designId}
              onDesignChange={setDesignId}
              abTestEnabled={abTestEnabled}
              onAbTestEnabledChange={(enabled) => {
                setAbTestEnabled(enabled);
                if (enabled && abTestDesignIds.length < 2) {
                  // sensible default: current design + every other design
                  setAbTestDesignIds(
                    Array.from(new Set([designId, ...abTestDesignIds]))
                  );
                }
              }}
              abTestDesignIds={abTestDesignIds}
              onAbTestDesignIdsChange={setAbTestDesignIds}
              onPreviewDesign={(id) => setPreviewModal({ designId: id })}
            />
          </div>

          {/* Options unique to the Cover design (only shown when the Cover
              design can actually be served: selected, or in the A/B rotation) */}
          {(
            abTestEnabled
              ? abTestDesignIds.includes("cover")
              : designId === "cover"
          ) ? (
            <div className="form-group mb-3 col-md-12">
              <div className="cover-design-options">
                <div className="cover-design-options-head">
                  <span className="cover-design-options-title">
                    Cover design options
                  </span>
                  <span className="cover-design-options-sub">
                    These settings only apply to the Cover design. The Landmark
                    header logo / footer checkboxes further down apply to the
                    Original design.
                  </span>
                </div>

                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="coverShowLandmarkHeaderText"
                    checked={coverOptions.showLandmarkHeaderText !== false}
                    onChange={(e) =>
                      setCoverOptions((prev) => ({
                        ...prev,
                        showLandmarkHeaderText: e.target.checked,
                      }))
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="coverShowLandmarkHeaderText"
                  >
                    Show "with Landmark Ventures" in the header
                  </label>
                </div>
                <div className="example-text">
                  Shows the text next to the client logos at the top-left of the
                  cover photo. Replaced by the SIS logo when "Show SIS Header
                  Logo" is enabled.
                </div>

                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="coverShowLandmarkFooterText"
                    checked={coverOptions.showLandmarkFooterText !== false}
                    onChange={(e) =>
                      setCoverOptions((prev) => ({
                        ...prev,
                        showLandmarkFooterText: e.target.checked,
                      }))
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="coverShowLandmarkFooterText"
                  >
                    Show "with Landmark Ventures" in the footer
                  </label>
                </div>
                <div className="example-text">
                  Shows the text next to the client logos at the bottom-left of
                  the page.
                </div>
              </div>
            </div>
          ) : null}

          {/* Page Content Subheading */}
          <div className="col-md-12">
            <h3>Page Content</h3>
          </div>

          {/* Header Logos input */}
          <div className="form-group mb-3 col-md-12">
            <label className="form-label">Header Logos</label>
            <div className="existing-logos-container">
              <label className="form-label">
                Click to add a previously used logo:
              </label>
              <div className="existing-logos-outer">
                {allExistingHeaderLogos.map((hl, index) => {
                  return (
                    <div
                      onClick={() => {
                        setHeaderLogos([...headerLogos, hl]); // add the new logo object
                      }}
                      key={index}
                      className="existing-logo"
                      style={{
                        maxWidth: `${scaleHeaderLogoMaxWidth(hl.scale) / 2.5}px`,
                      }}
                    >
                      <img
                        src={hl.src}
                        alt={`Header Logo ${index + 1}`}
                        className="img-fluid"
                        style={{
                          maxWidth: `${scaleHeaderLogoMaxWidth(hl.scale) / 2.5}px`,
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            {headerLogos.map((logo, index) => {
              const headerLogoStyle = {
                maxWidth: `${scaleHeaderLogoMaxWidth(logo.scale)}px`,
              };
              if (logo.yOffset && logo.yOffset !== 0) {
                headerLogoStyle.transform = `translateY(${logo.yOffset}px)`;
              }
              const darkMode = normalizeHeaderLogoDarkMode(logo.darkMode);
              const darkRender = getDarkLogoRender(logo);
              const darkGenerating =
                darkMode === "auto" &&
                !logo.darkSrc &&
                darkVariantInFlightRef.current.has(logo.src);
              const darkFailed =
                darkMode === "auto" &&
                !logo.darkSrc &&
                darkVariantFailedRef.current.has(logo.src);
              return (
                <div
                  key={index}
                  className="header-logo-row mb-2"
                  style={{ marginTop: `${index === 0 ? "0" : "16px"}` }}
                >
                <div className="d-flex align-items-center">
                  <div className="header-logo-container img-thumbnail">
                    <img
                      src={logo.src}
                      alt={`Header Logo ${index + 1}`}
                      className="img-fluid"
                      style={headerLogoStyle}
                    />
                  </div>
                  {/* Input to adjust the scale and yOffset of the image */}
                  <div className="controls-container">
                    <div className="scale-container">
                      {/* Scale Input */}
                      <label className="form-label mb-0">Scale %</label>
                      <input
                        type="number"
                        value={logo.scale}
                        onChange={(e) => {
                          const newScale = Math.max(
                            1,
                            Math.min(200, parseInt(e.target.value, 10) || 1)
                          );
                          const updatedLogos = headerLogos.map((l, i) =>
                            i === index ? { ...l, scale: newScale } : l
                          );
                          setHeaderLogos(updatedLogos);
                        }}
                        className="form-control scale-spinner"
                        min="1"
                        max="200"
                      />
                    </div>

                    <div className="yOffset-container">
                      {/* yOffset Input */}
                      <label className="form-label mb-0">Y Offset (px)</label>
                      <input
                        type="number"
                        value={logo.yOffset || 0}
                        onChange={(e) => {
                          const newYOffset = parseInt(e.target.value, 10) || 0;
                          const updatedLogos = headerLogos.map((l, i) =>
                            i === index ? { ...l, yOffset: newYOffset } : l
                          );
                          setHeaderLogos(updatedLogos);
                        }}
                        className="form-control yOffset-spinner"
                      />
                    </div>

                    <div className="link-container">
                      <label className="form-label mb-0">
                        Link URL (optional)
                      </label>
                      <input
                        type="text"
                        value={logo.link || ""}
                        placeholder="https://example.com"
                        onChange={(e) => {
                          const newLink = e.target.value;
                          const updatedLogos = headerLogos.map((l, i) =>
                            i === index ? { ...l, link: newLink } : l
                          );
                          setHeaderLogos(updatedLogos);
                        }}
                        className="form-control"
                        style={{ width: "220px" }}
                      />
                    </div>

                    {isPngLogoSrc(logo.originalSrc || logo.src)
                      ? renderTrimEmptySpaceToggle({
                          id: `header-logo-trim-${index}`,
                          checked: logo.trimEmptySpace,
                          trimming: logoTrimLoading[`header-${index}`],
                          onChange: (checked) =>
                            handleToggleTrimEmptySpace(
                              setHeaderLogos,
                              headerLogos,
                              index,
                              checked,
                              { isHeaderLogo: true }
                            ),
                        })
                      : null}

                    {/* Vertical "pipe" divider between this logo and the next */}
                    {index < headerLogos.length - 1 ? (
                      <div className="trim-empty-space-container">
                        <div className="trim-empty-space-input">
                          <label
                            htmlFor={`header-logo-separator-${index}`}
                            className="trim-empty-space-inline"
                            title="Draws a thin grey vertical line between this logo and the next one"
                          >
                            <input
                              id={`header-logo-separator-${index}`}
                              type="checkbox"
                              checked={!!logo.separatorAfter}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setHeaderLogos(
                                  headerLogos.map((l, i) =>
                                    i === index
                                      ? { ...l, separatorAfter: checked }
                                      : l
                                  )
                                );
                              }}
                            />
                            <span>Divider after logo</span>
                          </label>
                        </div>
                      </div>
                    ) : null}

                    <button
                      className="btn btn-danger ml-2"
                      onClick={() =>
                        removeArrayItem(setHeaderLogos, headerLogos, index)
                      }
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {/* Version used by designs with a dark header (e.g. Cover) */}
                <div className="header-logo-dark">
                  <div
                    className="header-logo-dark-swatch"
                    title="How this logo appears on dark designs"
                  >
                    {darkGenerating ? (
                      <span className="header-logo-dark-status">
                        Generating…
                      </span>
                    ) : (
                      <img
                        src={darkRender.src}
                        alt=""
                        className={darkRender.invert ? "is-inverted" : ""}
                        style={headerLogoStyle}
                      />
                    )}
                  </div>
                  <div className="header-logo-dark-controls">
                    <label className="form-label mb-0">
                      Dark design version
                    </label>
                    <select
                      className="form-control form-control-sm"
                      value={darkMode}
                      onChange={(e) =>
                        handleDarkModeChange(index, e.target.value)
                      }
                    >
                      <option value="auto">
                        Auto (dark text to white, icon kept)
                      </option>
                      <option value="custom">Upload my own dark version</option>
                      <option value="original">Use original logo as-is</option>
                    </select>
                    <div className="header-logo-dark-actions">
                      {darkMode === "auto" ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          disabled={darkGenerating}
                          onClick={() => regenerateDarkVariant(index)}
                        >
                          Regenerate
                        </button>
                      ) : null}
                      {darkMode === "custom" ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleDarkModeChange(index, "custom")}
                        >
                          {logo.darkSrc ? "Replace file" : "Choose file"}
                        </button>
                      ) : null}
                      {darkFailed ? (
                        <span className="text-danger small">
                          Could not generate automatically. Upload a dark
                          version or use the original.
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
                </div>
              );
            })}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const targetIndex = darkLogoUploadIndexRef.current;
                darkLogoUploadIndexRef.current = null;
                if (targetIndex === null || targetIndex === undefined) return;
                handleImageUpload(e, setHeaderLogos, {
                  compress: true,
                  headerLogoDarkMode: true,
                  headerLogoIndex: targetIndex,
                });
                e.target.value = "";
              }}
              className="d-none"
              id="headerLogoDarkUpload"
            />
            {loading && (
              <img
                src={imageUploadLoader}
                alt="Uploading..."
                width="100"
                className="img-thumbnail"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                handleImageUpload(e, setHeaderLogos, {
                  compress: true,
                  headerLogoMode: true,
                })
              }
              className="d-none"
              id="headerLogoUpload"
            />
            <button
              className="btn btn-secondary mt-2"
              onClick={() =>
                document.getElementById("headerLogoUpload").click()
              }
            >
              Upload Logo
            </button>
            {darkVariantsPending > 0 ? (
              <span className="header-logo-dark-pending">
                Generating dark design version
                {darkVariantsPending > 1 ? "s" : ""}…
              </span>
            ) : null}
          </div>

          {/* Hero Heading input (Full Width) */}
          <div className="form-group mb-3 col-md-12">
            <label className="form-label">Hero Heading</label>
            <input
              type="text"
              value={heroHeading}
              className="form-control"
              onChange={(e) => setHeroHeading(e.target.value)}
            />
          </div>

          {/* Hero Sub Heading input (Full Width) */}
          <div className="form-group mb-3 col-md-12">
            <label className="form-label">Hero Sub Heading</label>
            <textarea
              value={heroSubHeading}
              className="form-control"
              style={{
                height: "80px",
                lineHeight: "1.3",
              }}
              onChange={(e) => setHeroSubHeading(e.target.value)}
            />
            <div className="explainer-label">
              To add a link, type{" "}
              <em>[URL=https://example.com]link text[/URL]</em>
            </div>
          </div>

          {/* Hero Text input */}
          <div className="form-group mb-3 col-md-12">
            <label className="form-label">Hero Text</label>
            <textarea
              value={heroText}
              className="form-control"
              style={{ height: "120px", lineHeight: "1.3" }}
              onChange={(e) => setHeroText(e.target.value)}
            />
            <div className="explainer-label">
              To insert a line break, type <em>\n</em>. To add a link, type{" "}
              <em>[URL=https://example.com]link text[/URL]</em>
            </div>
          </div>

          {/* Expect Bullets input */}
          <div className="form-group mb-3 col-md-12">
            <label className="form-label">Timetable Bullets</label>
            {expectBullets.map((bullet, index) => (
              <div key={index} className="d-flex align-items-center mb-2">
                <input
                  type="text"
                  value={bullet}
                  className="form-control"
                  onChange={(e) => {
                    const updatedBullets = [...expectBullets];
                    updatedBullets[index] = e.target.value;
                    setExpectBullets(updatedBullets);
                  }}
                />
                <button
                  className="btn btn-danger ml-2"
                  onClick={() =>
                    removeArrayItem(setExpectBullets, expectBullets, index)
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              className="btn btn-secondary mt-2"
              onClick={() => addArrayItem(setExpectBullets, expectBullets, "")}
            >
              Add Bullet
            </button>
          </div>

          {/* Content Sections input */}
          <div className="form-group mb-3 col-md-12">
            <label className="form-label">Content Sections</label>
            <div className="explainer-label mb-2">
              Optional sections displayed below the timetable bullets - each
              with a bolded heading and a paragraph. To insert a line break in
              a paragraph, type <em>\n</em>. To add a link, type{" "}
              <em>[URL=https://example.com]link text[/URL]</em>
            </div>
            {contentSections.map((section, index) => (
              <div key={index} className="d-flex align-items-start mb-2">
                <div className="w-100">
                  <input
                    type="text"
                    placeholder="Heading"
                    value={section.heading}
                    className="form-control mb-2"
                    onChange={(e) => {
                      const updatedSections = contentSections.map((s, i) =>
                        i === index ? { ...s, heading: e.target.value } : s
                      );
                      setContentSections(updatedSections);
                    }}
                  />
                  <textarea
                    placeholder="Paragraph text"
                    value={section.text}
                    className="form-control mb-2"
                    style={{
                      height: "96px",
                      lineHeight: "1.3",
                    }}
                    onChange={(e) => {
                      const updatedSections = contentSections.map((s, i) =>
                        i === index ? { ...s, text: e.target.value } : s
                      );
                      setContentSections(updatedSections);
                    }}
                  />
                </div>
                <button
                  className="btn btn-danger"
                  onClick={() =>
                    removeArrayItem(setContentSections, contentSections, index)
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              className="btn btn-secondary mt-2"
              onClick={() =>
                addArrayItem(setContentSections, contentSections, {
                  heading: "",
                  text: "",
                })
              }
            >
              Add Section
            </button>
          </div>

          {/* What to Expect Text input */}
          <div className="form-group mb-3 col-md-12">
            <label className="form-label">What to Expect Text</label>
            <textarea
              value={expectText}
              className="form-control"
              style={{ height: "80px", lineHeight: "1.3" }}
              onChange={(e) => setExpectText(e.target.value)}
            />
          </div>

          {/* Event Images input */}
          <div className="form-group mb-3 col-md-12">
            <label className="form-label">Event Image</label>
            {eventImages.map((image, index) => (
              <div key={index} className="d-flex align-items-center mb-2">
                <img
                  src={image}
                  alt={`Event Image ${index + 1}`}
                  className="img-thumbnail"
                />
                <button
                  className="btn btn-danger ml-2"
                  onClick={() =>
                    removeArrayItem(setEventImages, eventImages, index)
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            {loading && (
              <img
                src={imageUploadLoader}
                alt="Uploading..."
                width="100"
                className="img-thumbnail"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                handleImageUpload(e, setEventImages, {
                  scaleWidth: 1200,
                  compress: true,
                })
              }
              className="d-none"
              id="eventImageUpload"
            />
            {eventImages && eventImages.length ? (
              <></>
            ) : (
              <button
                className="btn btn-secondary mt-2"
                onClick={() =>
                  document.getElementById("eventImageUpload").click()
                }
              >
                Upload Image
              </button>
            )}
          </div>

          {/* Event Location Short input */}
          <div className="form-group mb-3 col-md-6">
            <label className="form-label">Event Location (Short)</label>
            <input
              type="text"
              value={eventLocationShort}
              className="form-control"
              onChange={(e) => setEventLocationShort(e.target.value)}
            />
          </div>

          {/* Event Location Full input */}
          <div className="form-group mb-3 col-md-6">
            <label className="form-label">Event Location (Full)</label>
            <textarea
              value={eventLocationFull}
              className="form-control"
              style={{
                height: "84px",
                lineHeight: "1.3",
              }}
              onChange={(e) => setEventLocationFull(e.target.value)}
            />
          </div>

          {/* Event Date input */}
          <div className="form-group mb-3 col-md-6">
            <label className="form-label">Event Date</label>
            <div className="example-text">e.g. "Tuesday, September 10th"</div>
            <input
              type="text"
              value={eventDate}
              className="form-control"
              onChange={(e) => setEventDate(e.target.value)}
            />
          </div>

          {/* Event Time input */}
          <div className="form-group mb-3 col-md-6">
            <label className="form-label">Event Time</label>
            <div className="example-text">e.g. "6:00pm - 9:00pm CDT"</div>
            <input
              type="text"
              value={eventTime}
              className="form-control"
              onChange={(e) => setEventTime(e.target.value)}
            />
          </div>

          {/* Event Speaker input */}
          <div className="form-group mb-3 col-md-6">
            <label className="form-label">Event Speaker (Optional)</label>
            <div className="example-text">e.g. "Fiona McMann"</div>
            <input
              type="text"
              value={eventSpeaker}
              className="form-control"
              onChange={(e) => setEventSpeaker(e.target.value)}
            />
          </div>

          {/* Speaker Profiles */}
          <div className="form-group col-md-12 dynamic-check-option-container">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="enableSpeakerProfiles"
                checked={enableSpeakerProfiles}
                onChange={(e) =>
                  handleEnableSpeakerProfilesChange(e.target.checked)
                }
              />
              <label
                className="form-check-label"
                htmlFor="enableSpeakerProfiles"
              >
                Enable Speaker Profiles
              </label>
            </div>
          </div>
          <div className="example-text">
            When checked, each speaker below is displayed as a profile card
            (heading, photo, name, title and bio) in the sidebar beneath the
            event details. Speakers are shown in the order listed.
          </div>

          {enableSpeakerProfiles ? (
            <div className="custom-question-container mt-2">
              {speakerProfiles.map((speaker, index) => {
                const uploadInputId = `speakerProfileImageUpload-${index}`;
                return (
                  <div
                    key={index}
                    className="rounded p-3 mt-2"
                    style={{ backgroundColor: "rgba(255, 255, 255, 0.35)" }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <label className="form-label mb-0">
                        Speaker {index + 1}
                      </label>
                      <div className="d-flex align-items-center">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          style={{
                            padding: "2px 10px",
                            lineHeight: 1.2,
                            minWidth: 0,
                          }}
                          disabled={index === 0}
                          onClick={() => moveSpeakerProfile(index, -1)}
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          style={{
                            padding: "2px 10px",
                            lineHeight: 1.2,
                            minWidth: 0,
                            marginLeft: "8px",
                          }}
                          disabled={index === speakerProfiles.length - 1}
                          onClick={() => moveSpeakerProfile(index, 1)}
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          style={{ marginLeft: "16px" }}
                          onClick={() => removeSpeakerProfile(index)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    <div className="form-group mb-2">
                      <label className="form-label">Name</label>
                      <div className="example-text">e.g. "Dave Kellermanns"</div>
                      <input
                        type="text"
                        value={speaker.name}
                        className="form-control"
                        onChange={(e) =>
                          updateSpeakerProfile(index, "name", e.target.value)
                        }
                      />
                    </div>

                    <div className="form-group mb-2">
                      <label className="form-label">Heading (Optional)</label>
                      <div className="example-text">
                        e.g. "Thought Leader", "Fireside Chat", "Speaker"
                      </div>
                      <input
                        type="text"
                        value={speaker.heading}
                        className="form-control"
                        onChange={(e) =>
                          updateSpeakerProfile(index, "heading", e.target.value)
                        }
                      />
                    </div>

                    <div className="form-group mb-2">
                      <label className="form-label">Title (Optional)</label>
                      <div className="example-text">
                        e.g. "Global Advisor for Workload at Broadcom"
                      </div>
                      <input
                        type="text"
                        value={speaker.title}
                        className="form-control"
                        onChange={(e) =>
                          updateSpeakerProfile(index, "title", e.target.value)
                        }
                      />
                    </div>

                    <div className="form-group mb-2">
                      <label className="form-label">Bio (Optional)</label>
                      <textarea
                        value={speaker.bio}
                        className="form-control"
                        style={{ height: "120px", lineHeight: "1.3" }}
                        onChange={(e) =>
                          updateSpeakerProfile(index, "bio", e.target.value)
                        }
                      />
                      <div className="explainer-label">
                        To insert a line break, type <em>\n</em>. To add a link,
                        type <em>[URL=https://example.com]link text[/URL]</em>
                      </div>
                    </div>

                    <div className="form-group mb-0">
                      <label className="form-label">Photo (Optional)</label>
                      {speaker.image ? (
                        <div className="d-flex align-items-center mb-2">
                          <img
                            src={speaker.image}
                            alt={speaker.name || `Speaker ${index + 1}`}
                            className="img-thumbnail"
                            style={{ maxWidth: "160px" }}
                          />
                          <button
                            type="button"
                            className="btn btn-danger ml-2"
                            onClick={() =>
                              updateSpeakerProfile(index, "image", "")
                            }
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                      {loading && (
                        <img
                          src={imageUploadLoader}
                          alt="Uploading..."
                          width="100"
                          className="img-thumbnail"
                        />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleImageUpload(
                            e,
                            (url) => updateSpeakerProfile(index, "image", url),
                            {
                              scaleWidth: 600,
                              compress: true,
                              singleImageMode: true,
                            }
                          )
                        }
                        className="d-none"
                        id={uploadInputId}
                      />
                      {!speaker.image ? (
                        <button
                          type="button"
                          className="btn btn-secondary mt-1"
                          onClick={() =>
                            document.getElementById(uploadInputId).click()
                          }
                        >
                          Upload Photo
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
              <div className="d-flex justify-content-end mt-3">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={addSpeakerProfile}
                >
                  Add Speaker
                </button>
              </div>
            </div>
          ) : null}

          {/* FAQs input */}
          <div className="form-group mb-3 col-md-12">
            <label className="form-label">FAQs</label>
            {faqs.map((faq, index) => (
              <div key={index} className="d-flex align-items-start mb-2">
                <div className="w-100">
                  <input
                    type="text"
                    placeholder="Question"
                    value={faq.question}
                    className="form-control mb-2"
                    onChange={(e) => {
                      const updatedFaqs = [...faqs];
                      updatedFaqs[index].question = e.target.value;
                      setFaqs(updatedFaqs);
                    }}
                  />
                  <textarea
                    placeholder="Answer"
                    value={faq.answer}
                    className="form-control mb-2"
                    style={{
                      height: "72px",
                      lineHeight: "1.3",
                    }}
                    onChange={(e) => {
                      const updatedFaqs = [...faqs];
                      updatedFaqs[index].answer = e.target.value;
                      setFaqs(updatedFaqs);
                    }}
                  />
                </div>
                <button
                  className="btn btn-danger"
                  onClick={() => removeArrayItem(setFaqs, faqs, index)}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              className="btn btn-secondary mt-2"
              onClick={() =>
                addArrayItem(setFaqs, faqs, { question: "", answer: "" })
              }
            >
              Add FAQ
            </button>
          </div>
          {/* After RSVP Submitted Text input */}
          <div className="form-group mb-3 col-md-12">
            <label className="form-label">After RSVP Submitted Text</label>
            <textarea
              value={afterSubmittedText}
              className="form-control"
              style={{ height: "80px", lineHeight: "1.3" }}
              onChange={(e) => setAfterSubmittedText(e.target.value)}
            />
          </div>
          <div className="form-group mb-3 col-md-12">
            <label className="form-label">Footer Image</label>
            <div className="example-text">
              A full width image to be displayed at the bottom of the page.
            </div>
            {footerImages &&
              footerImages.map((image, index) => (
                <div key={index} className="d-flex align-items-center mb-2">
                  <img
                    src={image}
                    alt={`Footer Image ${index + 1}`}
                    className="img-thumbnail"
                  />
                  <button
                    className="btn btn-danger ml-2"
                    onClick={() =>
                      removeArrayItem(setFooterImages, footerImages, index)
                    }
                  >
                    Remove
                  </button>
                </div>
              ))}
            {loading && (
              <img
                src={imageUploadLoader}
                alt="Uploading..."
                width="100"
                className="img-thumbnail"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                handleImageUpload(e, setFooterImages, { compress: true })
              }
              className="d-none"
              id="footerImageUpload"
            />

            {footerImages && footerImages.length ? (
              <></>
            ) : (
              <button
                className="btn btn-secondary mt-2"
                onClick={() =>
                  document.getElementById("footerImageUpload").click()
                }
              >
                Upload Footer Image
              </button>
            )}
          </div>
          <div className="form-group mb-3 mt-3 col-md-12">
            <label className="form-label">Theme Color</label>
            <div className="example-text">
              This background color is used to accent any buttons and dropdown
              toggles.
            </div>
            <input
              type="color"
              value={themePrimaryColor}
              className="form-control"
              onChange={(e) => setThemePrimaryColor(e.target.value)}
              style={{ maxWidth: "100px" }}
            />
          </div>
          <div className="form-group mb-3 mt-3 col-md-12">
            <label className="form-label">Custom Text Styling</label>
            <div className="example-text">
              These CSS classes will be applied to all text elements. Typically
              used for setting custom fonts. Contact mjones to set up the
              styling.
            </div>
            <label className="form-label">Global Text Class</label>
            <input
              type="text"
              className="form-control"
              value={textClass}
              onChange={(e) => setTextClass(e.target.value)}
              // style={{ maxWidth: "100px" }}
            />
            <label className="form-label mt-2">Heading Text Class</label>
            <input
              type="text"
              className="form-control"
              value={headerTextClass}
              onChange={(e) => setHeaderTextClass(e.target.value)}
              // style={{ maxWidth: "100px" }}
            />
          </div>

          <div className="form-group col-md-12 dynamic-check-option-container">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="showAddToCalendarButton"
                checked={calendarOptions?.showAddToCalendarButton}
                onChange={(e) =>
                  setCalendarOptions((prev) => ({
                    ...prev,
                    showAddToCalendarButton: e.target.checked,
                  }))
                }
              />
              <label
                className="form-check-label"
                htmlFor="showAddToCalendarButton"
              >
                Create Calendar Invite
              </label>
            </div>
          </div>

          <div className="example-text">
            When checked, an "Add to Calendar" button will appear to the user
            after they submit an RSVP.
          </div>

          {calendarOptions?.showAddToCalendarButton ? (
            <div className="calendar-options-container">
              <div className="form-group col-md-12">
                <label className="form-label">Calendar Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={calendarOptions.title || ""}
                  onChange={(e) =>
                    setCalendarOptions((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="form-group col-md-12">
                <label className="form-label">Timezone</label>
                <select
                  className="form-control"
                  value={calendarOptions?.timeZone || ""}
                  onChange={(e) =>
                    setCalendarOptions((prev) => ({
                      ...prev,
                      timeZone: e.target.value,
                    }))
                  }
                >
                  <option value="" disabled>
                    Select a timezone
                  </option>
                  {timeZoneList.map((tz) => (
                    <option key={tz} value={tz}>
                      {formatTimeZone(tz)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="form-label">Start Date & Time</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={
                      calendarOptions.startDateTime
                        ? DateTime.fromISO(calendarOptions.startDateTime, {
                            zone:
                              calendarOptions?.timeZone || "America/New_York",
                          }).toFormat("yyyy-MM-dd'T'HH:mm")
                        : ""
                    }
                    onChange={(e) =>
                      setCalendarOptions((prev) => ({
                        ...prev,
                        startDateTime: DateTime.fromFormat(
                          e.target.value,
                          "yyyy-MM-dd'T'HH:mm",
                          {
                            zone:
                              calendarOptions?.timeZone || "America/New_York",
                          }
                        ).toISO(),
                      }))
                    }
                  />
                </div>

                <div className="form-group col-md-6">
                  <label className="form-label">End Date & Time</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={
                      calendarOptions.endDateTime
                        ? DateTime.fromISO(calendarOptions.endDateTime, {
                            zone:
                              calendarOptions?.timeZone || "America/New_York",
                          }).toFormat("yyyy-MM-dd'T'HH:mm")
                        : ""
                    }
                    onChange={(e) =>
                      setCalendarOptions((prev) => ({
                        ...prev,
                        endDateTime: DateTime.fromFormat(
                          e.target.value,
                          "yyyy-MM-dd'T'HH:mm",
                          {
                            zone:
                              calendarOptions?.timeZone || "America/New_York",
                          }
                        ).toISO(),
                      }))
                    }
                  />
                </div>
              </div>

              <div className="form-group col-md-12">
                <label className="form-label">Location</label>
                <input
                  type="text"
                  className="form-control"
                  value={calendarOptions.location || ""}
                  onChange={(e) =>
                    setCalendarOptions((prev) => ({
                      ...prev,
                      location: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="form-group col-md-12">
                <label className="form-label">
                  Calendar Description (HTML)
                </label>
                <div className="form-group mb-3 col-12">
                  {/* <div>Auto update is locked because the text has been manually edited</div> */}
                  <ErrorBoundary>
                    <Editor
                      value={calendarOptions.description || ""}
                      onValueChange={(code) => {
                        setCalendarOptions((prev) => ({
                          ...prev,
                          description: code,
                          manuallyEdited: true,
                        }));
                      }}
                      highlight={(code) => {
                        try {
                          return highlight(code, languages.markup);
                        } catch (error) {
                          console.error("Highlighting error:", error);
                          return code; // Return unformatted code as a fallback
                        }
                      }}
                      padding={15}
                      className="code-editor"
                      style={{
                        color: "#000",
                        fontSize: 11,
                        backgroundColor: "#fff",
                        border: "1px #0072ff solid",
                        borderRadius: "0.625rem",
                        fontFamily:
                          "ui-monospace,Roboto Mono,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
                      }}
                    />
                  </ErrorBoundary>
                </div>{" "}
                <label className="form-label">
                  Calendar Description Preview
                </label>
                <div className="col-12">
                  <div className="calendar-desc-viewer viewer">
                    <div className="body">
                      {parse(calendarOptions.description || "")}
                    </div>
                  </div>
                </div>{" "}
                {/* <div
                  className="download-calendar-link"
                  onClick={() => {
                    generateICSFile(calendarOptions);
                  }}
                >
                  Download Calendar Invite
                </div> */}
                {calendarOptions.eventProkey ? (
                  <AddToCalendarButton
                    proKey={calendarOptions.eventProkey}
                  ></AddToCalendarButton>
                ) : null}
                {/* <textarea
                  className="form-control"
                  rows={5}
                  value={calendarOptions.description || ""}
                  onChange={(e) =>
                    setCalendarOptions((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                /> */}
              </div>
            </div>
          ) : null}

          <div className="form-group col-md-12 dynamic-check-option-container">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="hideLandmarkHeaderLogo"
                checked={!hideLandmarkHeaderLogo}
                onChange={(e) => {
                  setHideLandmarkHeaderLogo(!e.target.checked);
                  if (e.target.checked) {
                    setShowSISHeaderLogo(false);
                  }
                }}
              />
              <label
                className="form-check-label"
                htmlFor="hideLandmarkHeaderLogo"
              >
                Show Landmark Header Logo
              </label>
            </div>
          </div>
          <div className="example-text">
            When checked, the Landmark header logo will be displayed. (Original
            design only — the Cover design has its own options under the page
            design picker.)
          </div>

          <div className="form-group col-md-12 dynamic-check-option-container">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="showSISHeaderLogo"
                checked={showSISHeaderLogo}
                onChange={(e) => {
                  setShowSISHeaderLogo(e.target.checked);
                  if (e.target.checked) {
                    setHideLandmarkHeaderLogo(true);
                  }
                }}
              />
              <label className="form-check-label" htmlFor="showSISHeaderLogo">
                Show SIS Header Logo
              </label>
            </div>
          </div>
          <div className="example-text">
            When checked, the Social Innovation Summit header logo will be
            displayed instead of the Landmark logo.
          </div>

          <div className="form-group col-md-12 dynamic-check-option-container">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="hideLandmarkFooter"
                checked={!hideLandmarkFooter}
                onChange={(e) => setHideLandmarkFooter(!e.target.checked)}
              />
              <label className="form-check-label" htmlFor="hideLandmarkFooter">
                Show Landmark Footer
              </label>
            </div>
          </div>
          <div className="example-text">
            When checked, the Landmark Ventures footer will be displayed.
            (Original design only.)
          </div>

          <div className="form-group col-md-12 dynamic-check-option-container">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="businessEmailsOnly"
                checked={businessEmailsOnly}
                onChange={(e) => setBusinessEmailsOnly(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="businessEmailsOnly">
                Business Emails Only
              </label>
            </div>
          </div>
          <div className="example-text">
            When checked, emails from free email providers (e.g. gmail.com,
            yahoo.com) won't be accepted on the RSVP form, and the user will be prompted to enter a business email.
          </div>

          <div className="form-group col-md-12 dynamic-check-option-container">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="hideDietaryInput"
                checked={hideDietaryInput}
                onChange={(e) => setHideDietaryInput(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="hideDietaryInput">
                Hide Dietary Restrictions Question
              </label>
            </div>
          </div>
          <div className="example-text">
            When checked, the 'Dietary Restrictions' form question will be
            hidden.
          </div>

          <div className="form-group col-md-12 dynamic-check-option-container">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="showCustomInput"
                checked={showCustomInput}
                onChange={(e) => handleShowCustomInputChange(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showCustomInput">
                Enable Custom Questions
              </label>
            </div>
          </div>
          <div className="example-text">
            Add one or more custom RSVP questions. Supported answer types are text,
            radio (single select), and checkbox (multi select).
          </div>

          {showCustomInput ? (
            <div className="custom-question-container mt-2">
              {customQuestions.map((question, index) => {
                const shouldShowOptions =
                  question.answerType === CUSTOM_QUESTION_TYPES.RADIO ||
                  question.answerType === CUSTOM_QUESTION_TYPES.CHECKBOX;
                const optionRows =
                  shouldShowOptions && question.options && question.options.length
                    ? question.options
                    : [""];

                return (
                  <div
                    key={question.questionId || index}
                    className="rounded p-3 mt-3"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.35)",
                      position: "relative",
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-danger position-absolute"
                      style={{ right: "-88px", top: "44px" }}
                      onClick={() => removeCustomQuestion(index)}
                    >
                      Remove
                    </button>

                    <div className="d-flex flex-wrap gap-3 align-items-end">
                      <div className="form-group w-100">
                        <label className="form-label">Question {index + 1}</label>
                        <input
                          type="text"
                          value={question.questionLabel}
                          className="form-control"
                          onChange={(e) =>
                            updateCustomQuestion(index, "questionLabel", e.target.value)
                          }
                        />
                      </div>

                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div className="form-group mb-0" style={{ minWidth: "250px" }}>
                        <label className="form-label">Answer Type</label>
                        <select
                          value={question.answerType}
                          className="form-select"
                          style={{ height: "46px", color: "#000" }}
                          onChange={(e) =>
                            updateCustomQuestion(index, "answerType", e.target.value)
                          }
                        >
                          <option value={CUSTOM_QUESTION_TYPES.TEXT}>Text</option>
                          <option value={CUSTOM_QUESTION_TYPES.RADIO}>Radio (Single choice)</option>
                          <option value={CUSTOM_QUESTION_TYPES.CHECKBOX}>
                            Checkbox (Multiple choice)
                          </option>
                        </select>
                        </div>

                        <div
                          className="form-group dynamic-check-option-container mb-0"
                          style={{
                            minHeight: "46px",
                            marginTop: "31px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <div className="form-check mb-0 d-inline-flex align-items-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`customInputRequired-${index}`}
                              checked={!!question.required}
                              onChange={(e) =>
                                updateCustomQuestion(index, "required", e.target.checked)
                              }
                            />
                            <label
                              className="form-check-label fw-normal"
                              style={{ transform: "translateY(3px)" }}
                              htmlFor={`customInputRequired-${index}`}
                            >
                              Make Required
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    {shouldShowOptions ? (
                      <>
                        <div className="form-group col-md-12 mt-2">
                          <label className="form-label">Options</label>
                          {optionRows.map((optionValue, optionIndex) => (
                            <div
                              key={`${question.questionId || index}-option-${optionIndex}`}
                              className="d-flex align-items-center gap-2 mt-2"
                            >
                              <input
                                type="text"
                                className="form-control"
                                placeholder={`Option ${optionIndex + 1}`}
                                value={optionValue}
                                onChange={(e) =>
                                  updateCustomQuestionOptionValue(
                                    index,
                                    optionIndex,
                                    e.target.value
                                  )
                                }
                              />
                              {optionRows.length > 1 ? (
                                <button
                                  type="button"
                                  className="btn btn-danger"
                                  onClick={() =>
                                    removeCustomQuestionOption(index, optionIndex)
                                  }
                                >
                                  Remove
                                </button>
                              ) : null}
                            </div>
                          ))}
                        </div>
                        <div className="mt-2">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm custom-question-add-option-btn"
                            onClick={() => addCustomQuestionOption(index)}
                          >
                            + Add Option
                          </button>
                        </div>
                        <div className="example-text mt-2">
                          These options will be shown as{" "}
                          {question.answerType === CUSTOM_QUESTION_TYPES.RADIO
                            ? "radio buttons (single choice)"
                            : "checkboxes (multiple choices)"}{" "}
                          on the RSVP form.
                        </div>
                      </>
                    ) : null}
                  </div>
                );
              })}

              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={addCustomQuestion}
                >
                  Add Custom Question
                </button>
              </div>
            </div>
          ) : null}

          <div className="form-group col-md-12 dynamic-check-option-container">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="enableLogoCarousel"
                checked={enableLogoCarousel}
                onChange={(e) => setEnableLogoCarousel(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="enableLogoCarousel">
                Enable Logo Carousel
              </label>
            </div>
          </div>
          <div className="example-text">
            When checked, the uploaded logos will be displayed in a carousel.
            Full-width shows them above the FAQs; Sidebar shows them in the event
            details rail.
          </div>

          {enableLogoCarousel ? (
            <div className="custom-question-container mt-2">
              <div
                className="rounded p-3 mt-2"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.35)" }}
              >
                <div className="form-group mb-3" style={{ maxWidth: "300px" }}>
                  <label className="form-label">Carousel Type</label>
                  <select
                    value={logoCarouselType}
                    className="form-select"
                    style={{ height: "46px", color: "#000" }}
                    onChange={(e) => setLogoCarouselType(e.target.value)}
                  >
                    <option value="fullwidth">Full-width</option>
                    <option value="sidebar">Sidebar</option>
                  </select>
                </div>

                {logoCarouselType === "sidebar" ? (
                  <>
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Sidebar Heading (Optional)
                      </label>
                      <input
                        type="text"
                        value={logoCarouselSidebarHeading}
                        className="form-control"
                        onChange={(e) =>
                          setLogoCarouselSidebarHeading(e.target.value)
                        }
                      />
                    </div>
                    <div className="form-group dynamic-check-option-container mb-3">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="logoCarouselSidebarHeadingUseThemeColor"
                          checked={logoCarouselSidebarHeadingUseThemeColor}
                          onChange={(e) =>
                            setLogoCarouselSidebarHeadingUseThemeColor(
                              e.target.checked
                            )
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="logoCarouselSidebarHeadingUseThemeColor"
                        >
                          Use Theme Color for Sidebar Heading
                        </label>
                      </div>
                      <div className="example-text">
                        When checked, the sidebar heading text uses the event's
                        theme color.
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="form-group dynamic-check-option-container mb-3">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="logoCarouselAnimated"
                        checked={logoCarouselAnimated}
                        onChange={(e) =>
                          setLogoCarouselAnimated(e.target.checked)
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="logoCarouselAnimated"
                      >
                        Animate Carousel
                      </label>
                    </div>
                    <div className="example-text">
                      When checked, the logos will continuously scroll
                      horizontally. When unchecked, the logos are displayed
                      statically (default).
                    </div>
                  </div>
                )}

                <label className="form-label">Carousel Logos</label>
                {logoCarouselImages.map((logo, index) => {
                  const baseThumbHeight = 70; // px at scale 100
                  return (
                    <div
                      key={index}
                      className="d-flex align-items-center mb-2"
                      style={{ marginTop: `${index === 0 ? "0" : "16px"}` }}
                    >
                      <div className="header-logo-container img-thumbnail">
                        <img
                          src={logo.src}
                          alt={`Carousel Logo ${index + 1}`}
                          className="img-fluid"
                          style={{
                            maxHeight: `${
                              (baseThumbHeight * (logo.scale || 100)) / 100
                            }px`,
                            width: "auto",
                          }}
                        />
                      </div>
                      <div className="controls-container">
                        <div className="scale-container">
                          <label className="form-label mb-0">Scale %</label>
                          <input
                            type="number"
                            value={logo.scale}
                            onChange={(e) => {
                              const newScale = Math.max(
                                1,
                                Math.min(200, parseInt(e.target.value, 10) || 1)
                              );
                              const updatedLogos = logoCarouselImages.map(
                                (l, i) =>
                                  i === index ? { ...l, scale: newScale } : l
                              );
                              setLogoCarouselImages(updatedLogos);
                            }}
                            className="form-control scale-spinner"
                            min="1"
                            max="200"
                          />
                        </div>
                        {isPngLogoSrc(logo.originalSrc || logo.src)
                          ? renderTrimEmptySpaceToggle({
                              id: `carousel-logo-trim-${index}`,
                              checked: logo.trimEmptySpace,
                              trimming: logoTrimLoading[`carousel-${index}`],
                              onChange: (checked) =>
                                handleToggleTrimEmptySpace(
                                  setLogoCarouselImages,
                                  logoCarouselImages,
                                  index,
                                  checked
                                ),
                            })
                          : null}
                        <button
                          className="btn btn-danger ml-2"
                          onClick={() =>
                            removeArrayItem(
                              setLogoCarouselImages,
                              logoCarouselImages,
                              index
                            )
                          }
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
                {loading && (
                  <img
                    src={imageUploadLoader}
                    alt="Uploading..."
                    width="100"
                    className="img-thumbnail"
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleImageUpload(e, setLogoCarouselImages, {
                      compress: true,
                      logoCarouselMode: true,
                    })
                  }
                  className="d-none"
                  id="logoCarouselUpload"
                />
                <button
                  className="btn btn-secondary mt-2"
                  onClick={() =>
                    document.getElementById("logoCarouselUpload").click()
                  }
                >
                  Upload Logo
                </button>
              </div>
            </div>
          ) : null}

          <div className="form-group col-md-6 mt-3">
            <label className="form-label">RSVP Button Text</label>
            <input
              type="text"
              value={rsvpButtonText}
              className="form-control"
              onChange={(e) => setRsvpButtonText(e.target.value)}
            />
          </div>

          <div className="form-group col-md-6 mt-3">
            <label className="form-label">Register Button Text</label>
            <input
              type="text"
              value={registerButtonText}
              className="form-control"
              onChange={(e) => setRegisterButtonText(e.target.value)}
            />
          </div>

          <div className="form-group col-md-12 dynamic-check-option-container">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="rsvpButtonUrlRedirect"
                checked={rsvpButtonUrlRedirect}
                onChange={(e) => setRsvpButtonUrlRedirect(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="rsvpButtonUrlRedirect">
                RSVP Button URL Redirect
              </label>
            </div>
          </div>
          <div className="example-text">
            When checked, clicking RSVP will open the specified URL instead of
            opening the RSVP form.
          </div>
          {rsvpButtonUrlRedirect ? (
            <div className="form-group col-md-12 mt-2">
              <label className="form-label">RSVP Redirect URL</label>
              <input
                type="text"
                value={rsvpButtonUrl}
                className="form-control"
                onChange={(e) => setRsvpButtonUrl(e.target.value)}
              />
            </div>
          ) : null}

          {/* Google Policy Options Checkbox */}
          <div className="form-group col-md-12 dynamic-check-option-container">
            {/* <label className="form-label">Google Policy Options</label> */}
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="googlePolicyOptions"
                checked={googlePolicyOptions}
                onChange={(e) => handleGooglePolicyChange(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="googlePolicyOptions">
                Show Google Options
              </label>
            </div>
          </div>
          <div className="example-text">
            When checked, the RSVP form will include the Google Cloud data
            policy disclaimer and the optional 'Receive Marketing from Google'
            checkbox. If your event is not associated with Google then uncheck
            this box.
          </div>

          {/* Google Policy Options Checkbox */}
          <div className="form-group col-md-12 dynamic-check-option-container">
            {/* <label className="form-label">Google Policy Options</label> */}
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="rubrikPolicyOptions"
                checked={rubrikPolicyOptions}
                onChange={(e) => handleRubrikPolicyChange(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="rubrikPolicyOptions">
                Show Rubrik Options
              </label>
            </div>
          </div>
          <div className="example-text">
            When checked, the RSVP form will include the Rubrik data
            policy disclaimer and the optional 'Receive Marketing from Rubrik'
            checkbox. If your event is not associated with Rubrik then uncheck
            this box.
          </div>

          {/* Apollo Autofill Checkbox */}
          <div className="form-group col-md-12 dynamic-check-option-container">
            {/* <label className="form-label">Apollo Autofill</label> */}
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="apolloAutofill"
                checked={apolloAutofill}
                onChange={(e) => setApolloAutofill(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="apolloAutofill">
                Enable Apollo Autofill
              </label>
            </div>
            <div className="example-text">
              When checked, the RSVP form will attempt to autofill the user's
              data by searching their email on the Apollo database. Each search
              uses an Apollo credit so we recommending disabling this for large
              events where we are expecting 500+ RSVPs.
            </div>
          </div>

          {/* Referral URLs Management */}
          <div
            className="form-group mb-3 col-md-12"
            style={{ marginTop: "20px" }}
          >
            <label className="form-label">Referral URLs</label>
            <div className="example-text">
              When users sign up through these URLs, we can track who referred
              them. The referral URL can be composed of random characters or
              personalized with the client’s name (e.g. 'atomiton'). If you'd
              prefer the URL to be less obvious, you can use generic terms like
              'event' or 'sign-up' to make it appear more discreet and avoid
              revealing the tracking purpose.
            </div>
            {referralUrls.map((referral, index) => {
              let thisReferralUrlValid = referral.url.trim().length;
              let thisReferralNameValid = referral.name.trim().length;

              // check for existing (duplicate) url
              if (
                referralUrls.filter(
                  (r) =>
                    r.url.trim().toLowerCase() ===
                    referral.url.trim().toLowerCase()
                ).length > 1
              ) {
                thisReferralUrlValid = false;
              }

              return (
                <div
                  key={index}
                  className="d-flex align-items-start mb-2 p-relative"
                  style={{ marginTop: `${index === 0 ? "0" : "25px"}` }} // add some margin for each ref url
                >
                  <div className="w-100">
                    {/* URL input with prefix */}
                    {/* <label className="form-label">URL</label> */}

                    <div className="input-group mb-2 ">
                      <span className="input-group-text ref-url">
                        https://events.landmarkventures.com/{pageUrl}/
                      </span>
                      <input
                        type="text"
                        value={referral.url}
                        className={`form-control page-url ${
                          thisReferralUrlValid ? "" : "invalid"
                        }`}
                        onChange={(e) => {
                          const updatedUrls = [...referralUrls];
                          updatedUrls[index].url = sanitizePageUrl(
                            e.target.value
                          );
                          setReferralUrls(updatedUrls);
                        }}
                      />
                    </div>
                    {/* <label className="form-label">Name</label> */}
                    <div className="example-text">
                      Name to appear in the 'Referred By' column in our backend
                      when the URL is used:
                    </div>
                    <input
                      type="text"
                      value={referral.name}
                      className={`form-control ref-name mb-2 ${
                        thisReferralNameValid ? "" : "invalid"
                      }`}
                      placeholder="Referral Name"
                      onChange={(e) => {
                        const updatedUrls = [...referralUrls];
                        updatedUrls[index].name = e.target.value;
                        setReferralUrls(updatedUrls);
                      }}
                    />
                  </div>
                  <button
                    className="btn btn-danger remove-ref-url-btn"
                    onClick={() =>
                      removeArrayItem(setReferralUrls, referralUrls, index)
                    }
                  >
                    Remove
                  </button>
                </div>
              );
            })}
            {/* Add new Referral URL button */}
            <button
              className="btn btn-secondary mt-2"
              onClick={() =>
                addArrayItem(setReferralUrls, referralUrls, {
                  url: "",
                  name: "",
                })
              }
            >
              {referralUrls && referralUrls.length
                ? "Add Another Referral URL"
                : "Add Referral URL"}
            </button>
          </div>

          {/* Event Owners */}
          <div
            className="form-group mb-3 col-md-12"
            style={{ marginTop: "20px" }}
          >
            <label className="form-label">
              Receive Notification on Sign Up
            </label>
            <div className="example-text">
              The following email addresses will receive an email notification
              whenever a form submission is received.
            </div>
            {owners.map((owner, index) => {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              let thisEmailValid = emailRegex.test(owner.email);

              // check for existing (duplicate) email
              if (
                owners.filter(
                  (o) =>
                    o.email.trim().toLowerCase() ===
                    owner.email.trim().toLowerCase()
                ).length > 1
              ) {
                thisEmailValid = false;
              }
              return (
                <div key={index} className="d-flex align-items-center mb-2">
                  <input
                    type="text"
                    value={owner.email}
                    className={`form-control owner-email ${
                      thisEmailValid ? "" : "invalid"
                    }`}
                    onChange={(e) => {
                      const updatedOwners = [...owners];
                      updatedOwners[index] = {
                        email: e.target.value.trim(),
                        assignedTime: timeNow(),
                      };
                      setOwners(updatedOwners);
                    }}
                  />
                  <button
                    className="btn btn-danger ml-2"
                    onClick={() => removeArrayItem(setOwners, owners, index)}
                  >
                    Remove
                  </button>
                </div>
              );
            })}
            <button
              className="btn btn-secondary mt-2"
              onClick={() =>
                addArrayItem(setOwners, owners, {
                  email: "",
                  assignedTime: timeNow(),
                })
              }
            >
              Add Email
            </button>
          </div>

          {/* Apollo Autofill Checkbox */}
          <div className="form-group col-md-12 dynamic-check-option-container">
            {/* <label className="form-label">Apollo Autofill</label> */}
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="registrationClosed"
                checked={registrationClosed}
                onChange={(e) => setRegistrationClosed(e.target.checked)}
              />
              <label
                className="form-check-label close-registrations-txt"
                htmlFor="registrationClosed"
              >
                Close Registrations
              </label>
            </div>
            <div className="example-text">
              When checked, no new RSVPs will be accepted, and the page will
              display a "Registration is now closed" message.
            </div>
          </div>
        </div>

        <div className="divider"></div>

        {/* Language versions (AI generated) */}
        <div className="row">
          <div className="form-group mb-3 col-md-12">
            <DynamicEventTranslations
              employee={employee}
              eventId={selectedEvent?.eventId || ""}
              pageUrl={pageUrl.trim()}
              translations={translations}
              onTranslationsChange={(next) => {
                setTranslations(next);
                getEvents && getEvents();
              }}
              contentChangedSinceLoad={translationsWillGoStale}
              hasUnsavedChanges={hasUnsavedChanges}
              onSaveChanges={({ lang } = {}) =>
                handleSaveEvent({ stayOpen: true, pendingLang: lang || "" })
              }
              autoGenerateLang={autoGenerateLang}
              onAutoGenerateHandled={onAutoGenerateHandled}
              onPreviewLang={(lang) => setPreviewModal({ lang })}
            />
          </div>
        </div>

        <div className="divider"></div>
        {translationsWillGoStale ? (
          <div className="d-flex align-items-center justify-content-center mb-2">
            <div className="translations-stale-msg mb-3">
              <i className="fa-solid fa-triangle-exclamation" /> Saving will mark{" "}
              {translations.map((t) => t.label || t.lang.toUpperCase()).join(", ")}{" "}
              out of date — regenerate after saving.
            </div>
          </div>
        ) : null}
        {!referralUrlValid ? (
          <div className="d-flex align-items-center justify-content-center mb-2">
            <div className="page-url-invalid-msg mb-3">
              {referralUrlInvalidMsg}
            </div>
          </div>
        ) : null}

        {!ownerEmailValid ? (
          <div className="d-flex align-items-center justify-content-center mb-2">
            <div className="page-url-invalid-msg mb-3">
              {ownerEmailInvalidMsg}
            </div>
          </div>
        ) : null}
        {!pageUrlValid ? (
          <div className="d-flex align-items-center justify-content-center mb-2">
            <div className="page-url-invalid-msg mb-3">{pageUrlInvalidMsg}</div>
          </div>
        ) : null}
        <div className="d-flex align-items-center justify-content-center mb-2">
          <button
            className="create-event-btn page-preview-btn btn btn-primary mb-3 "
            onClick={toggleShowPreview}
          >
            {`${showPreview ? "Hide Page Preview" : "Show Page Preview"}`}
            {showPreview ? (
              <i className="event-owner-add fa-sharp fa-solid fa-eye-slash ml-05"></i>
            ) : (
              <i className="event-owner-add fa-sharp fa-solid fa-eye ml-05"></i>
            )}
          </button>
          {referralUrlValid && ownerEmailValid && pageUrlValid ? (
            <button
              className="create-event-btn save-event-btn btn btn-primary mb-3 "
              onClick={handleSaveEvent}
            >
              {selectedEvent === null ? "Save Event" : "Save Changes"}
              <i className="event-owner-add fa-sharp fa-solid fa-check ml-05"></i>
            </button>
          ) : null}
        </div>
        <DynamicEventAssistant
          employee={employee}
          mode={selectedEvent && selectedEvent.eventId ? "edit" : "create"}
          getCurrentDraft={getEventObj}
          onApplyPatch={applyAssistantPatch}
          canSave={referralUrlValid && ownerEmailValid && pageUrlValid}
        />
      </div>
      {showPreview ? (
        <div className="dynamic-event-preview-container">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <DynamicEventPreview
              getEventObj={getEventObj}
              translations={translations}
              designId={designId}
              abTestEnabled={abTestEnabled}
              abTestDesignIds={abTestDesignIds}
            />
          </motion.div>
        </div>
      ) : null}
      <DynamicEventPreviewModal
        show={!!previewModal}
        onHide={() => setPreviewModal(null)}
        getEventObj={getEventObj}
        translations={translations}
        designId={designId}
        abTestEnabled={abTestEnabled}
        abTestDesignIds={abTestDesignIds}
        initialDesignId={previewModal?.designId}
        initialLang={previewModal?.lang}
      />
    </>
  );
};

const AddEditEvent = ({ events = [], getEvents, employee }) => {
  const [showForm, setShowForm] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedTemplateEvent, setSelectedTemplateEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  // { pageUrl, referralUrls: [{name,url}], translations: [{lang,label,stale}] }
  const [modalEventInfo, setModalEventInfo] = useState({
    pageUrl: "",
    referralUrls: [],
    translations: [],
  });

  // paging: show the most recent EVENTS_PAGE_SIZE, "Show more" adds another page
  const [editVisibleCount, setEditVisibleCount] = useState(EVENTS_PAGE_SIZE);
  const [templateVisibleCount, setTemplateVisibleCount] =
    useState(EVENTS_PAGE_SIZE);

  // NEW: sort events by createdTime descending
  const sortedEvents = useMemo(() => {
    if (!Array.isArray(events)) return [];
    return [...events].sort(
      (a, b) => (b.createdTime || 0) - (a.createdTime || 0)
    );
  }, [events]);

  // visible subsets
  const editVisibleEvents = sortedEvents.slice(0, editVisibleCount);
  const templateVisibleEvents = sortedEvents.slice(0, templateVisibleCount);
  const editRemaining = Math.max(0, sortedEvents.length - editVisibleCount);
  const templateRemaining = Math.max(
    0,
    sortedEvents.length - templateVisibleCount
  );

  function updateSetShowModal(show, info) {
    if (info) {
      // accept a bare pageUrl string or { pageUrl, referralUrls, translations }
      const next = typeof info === "string" ? { pageUrl: info } : info;
      setModalEventInfo({
        pageUrl: next.pageUrl || "",
        referralUrls: Array.isArray(next.referralUrls) ? next.referralUrls : [],
        translations: Array.isArray(next.translations) ? next.translations : [],
      });
    }
    setShowModal(show);
  }

  const copyUrl = (url) => {
    copyTextToClipboard(url);
    toast.success("URL copied to clipboard");
  };

  const toggleSetShowForm = () => {
    setShowForm(!showForm);
  };

  const handleSetCreateMode = () => {
    setEditMode(false);
    setCreateMode(true);
    setSelectedEvent(null);
    setSelectedTemplateEvent(null);
  };

  const handleSetEditMode = () => {
    setCreateMode(false);
    setEditMode(true);
    setSelectedTemplateEvent(null);
  };

  const resetModes = () => {
    setCreateMode(false);
    setEditMode(false);
    setSelectedEvent(null);
    setSelectedTemplateEvent(null);
  };

  // After "Save & generate" on a brand-new event: keep working on it in
  // edit mode so it has an eventId for language generation.
  const [pendingGenerateLang, setPendingGenerateLang] = useState("");
  const handleSavedStayOpen = (savedEvent, pendingLang = "") => {
    setPendingGenerateLang(pendingLang || "");
    setSelectedTemplateEvent(null);
    setSelectedEvent(savedEvent);
    setCreateMode(false);
    setEditMode(true);
  };

  console.log(`selectedEvent`, selectedEvent);

  return (
    <>
      <div className="add-edit-btn-container">
        {!showForm && (
          <>
            <button
              className={`create-event-btn btn btn-sm btn-primary`}
              onClick={handleSetCreateMode}
            >
              {`Create New Event`}
              <i className="event-owner-add fa-sharp fa-solid fa-plus ml-05"></i>
            </button>
            <button
              className={`create-event-btn btn btn-sm btn-primary`}
              onClick={handleSetEditMode}
            >
              {`Edit Existing Event`}
              <i className="event-owner-add fa-sharp fa-solid fa-pencil ml-05"></i>
            </button>
          </>
        )}
      </div>

      {editMode ? (
        <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
          <Card className="mb-3 h-fc bot-table">
            <div className="row">
              <div className="outputHeader">Edit Event</div>
              <>
                <div className="events-owners-subheading d-flex align-items-center justify-content-center">
                  <span>Select Event to Edit</span>
                </div>
                <div
                  className="event-owners-wrapper"
                  style={{ marginBottom: "12px" }}
                >
                  {editVisibleEvents.map((event) => {
                    const selectedClass =
                      selectedEvent &&
                      event.eventId === selectedEvent.eventId
                        ? "selected"
                        : "";
                    return (
                      <div key={event.eventId}>
                        <div
                          className={`event-owner-container selectable ${selectedClass}`}
                          onClick={() => setSelectedEvent(event)}
                        >
                          <div className="event-title">{event.title}</div>
                          <div className="event-title blue-text">
                            {`/${event.pageUrl}`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {editRemaining > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary ms-2"
                      onClick={() =>
                        setEditVisibleCount((c) => c + EVENTS_PAGE_SIZE)
                      }
                    >
                      {`Show more events (${editRemaining} more)`}
                    </button>
                  )}
                </div>
                {selectedEvent ? (
                  <EventForm
                    key={selectedEvent.eventId}
                    selectedEvent={selectedEvent}
                    getEvents={getEvents}
                    events={events}
                    employee={employee}
                    updateSetShowModal={updateSetShowModal}
                    resetModes={resetModes}
                    onSavedStayOpen={handleSavedStayOpen}
                    autoGenerateLang={pendingGenerateLang}
                    onAutoGenerateHandled={() => setPendingGenerateLang("")}
                  />
                ) : null}
              </>
            </div>
          </Card>
        </motion.div>
      ) : null}

      {createMode ? (
        <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
          <Card className="mb-3 h-fc bot-table">
            <div className="row">
              <div className="outputHeader">Create VIP Dinner Event</div>
              <>
                <div className="events-owners-subheading d-flex align-items-center justify-content-center">
                  <span>Load settings from existing event</span>
                </div>
                <div
                  className="event-owners-wrapper"
                  style={{ marginBottom: "12px" }}
                >
                  {templateVisibleEvents.map((event) => {
                    const selectedClass =
                      selectedTemplateEvent &&
                      event.eventId === selectedTemplateEvent.eventId
                        ? "selected"
                        : "";
                    return (
                      <div key={event.eventId}>
                        <div
                          className={`event-owner-container selectable ${selectedClass}`}
                          onClick={() => setSelectedTemplateEvent(event)}
                        >
                          <div className="event-title">{event.title}</div>
                          <div className="event-title blue-text">
                            {`/${event.pageUrl}`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {templateRemaining > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary ms-2"
                      onClick={() =>
                        setTemplateVisibleCount((c) => c + EVENTS_PAGE_SIZE)
                      }
                    >
                      {`Show more events (${templateRemaining} more)`}
                    </button>
                  )}
                </div>
                <EventForm
                  selectedEvent={selectedEvent}
                  selectedTemplateEvent={selectedTemplateEvent}
                  getEvents={getEvents}
                  employee={employee}
                  events={events}
                  updateSetShowModal={updateSetShowModal}
                  resetModes={resetModes}
                  onSavedStayOpen={handleSavedStayOpen}
                />
              </>
            </div>
          </Card>
        </motion.div>
      ) : null}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        dialogClassName="event-saved-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Event Saved</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="modal-event-header-msg">
            Event has been saved successfully!
          </div>

          <br />
          <div className="modal-view-landing-text">
            View the landing page here:{" "}
            <a
              href={`${PUBLIC_EVENTS_HOST}/${modalEventInfo.pageUrl}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {`${PUBLIC_EVENTS_HOST}/${modalEventInfo.pageUrl}`}
            </a>
            <span
              className="clipboard-icon hvr-shrink"
              title="Copy URL"
              onClick={() =>
                copyUrl(`${PUBLIC_EVENTS_HOST}/${modalEventInfo.pageUrl}`)
              }
            >
              <FileCopyOutlinedIcon style={{ width: "0.7em", height: "0.7em" }} />
            </span>
          </div>

          {modalEventInfo.referralUrls.length ? (
            <div className="modal-view-landing-text modal-view-extra-links">
              <div className="modal-links-title">Referral versions:</div>
              {modalEventInfo.referralUrls.map((r) => {
                const url = `${PUBLIC_EVENTS_HOST}/${modalEventInfo.pageUrl}/${r.url}`;
                return (
                  <div className="modal-link-row" key={`ref-${r.url}`}>
                    <span className="modal-link-name">{r.name}:</span>{" "}
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      {url}
                    </a>
                    <span
                      className="clipboard-icon hvr-shrink"
                      title="Copy URL"
                      onClick={() => copyUrl(url)}
                    >
                      <FileCopyOutlinedIcon
                        style={{ width: "0.7em", height: "0.7em" }}
                      />
                    </span>
                  </div>
                );
              })}
            </div>
          ) : null}

          {modalEventInfo.translations.length ? (
            <div className="modal-view-landing-text modal-view-extra-links">
              <div className="modal-links-title">Language versions:</div>
              {modalEventInfo.translations.map((t) => {
                const url = `${PUBLIC_EVENTS_HOST}/${modalEventInfo.pageUrl}?lang=${t.lang}`;
                return (
                  <div className="modal-link-row" key={`lang-${t.lang}`}>
                    <span className="modal-link-name">
                      {t.label || `${t.lang}`.toUpperCase()}
                      {t.stale ? (
                        <span
                          className="lang-stale-flag"
                          title="Out of date — regenerate in the editor"
                        >
                          {" "}
                          (out of date)
                        </span>
                      ) : null}
                      :
                    </span>{" "}
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      {url}
                    </a>
                    <span
                      className="clipboard-icon hvr-shrink"
                      title="Copy URL"
                      onClick={() => copyUrl(url)}
                    >
                      <FileCopyOutlinedIcon
                        style={{ width: "0.7em", height: "0.7em" }}
                      />
                    </span>
                  </div>
                );
              })}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className="btn btn-primary modal-view-event-close-btn"
            onClick={() => setShowModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddEditEvent;
